import { FormEvent, lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { createRepository } from "./lib/repository-factory";
import { demoMode, supabase, workspaceId } from "./lib/supabase";
import { handleNavigation } from "./lib/navigation";
import { actionsForEmbeddedTicket, embeddedActionUrl, getEmbedContext } from "./lib/embed";
import { displayDate, shiftTokyoDate, todayInTokyo } from "./lib/date";
import { actionsForDaily, candidatesForToday, mergeNewActionIdsAtTop } from "./lib/daily";
import { actionTicketBlockRoots, actionsForLaneGroup, effectiveActionLane, orderActionRootsInTicketBlocks, orderActionsWithChildren } from "./lib/actions";
import { compareMemos, memoDisplayTitle, memoPreviewBody } from "./lib/memos";
import { ticketFolderName } from "./lib/text";
import { mergeTicketFieldsIntoMarkdown } from "./lib/ticket-markdown";
import { hasLocalBridgeConnection, listLocalTicketFiles, openLocalTicketFile, pairLocalBridge, type LocalTicketFile } from "./lib/local-bridge";
import type { Action, ActionLane, ActionMoveDestination, AppView, DashboardData, Memo, ReminderMode, Ticket, TicketCatalog, TicketFile } from "./types";
import { TicketPicker } from "./components/TicketPicker";

const MemoMarkdownEditor = lazy(() => import("./components/MemoMarkdownEditor").then((module) => ({ default: module.MemoMarkdownEditor })));
const repository = createRepository();
const laneLabels: Record<ActionLane, string> = { urgent: "急ぎ", later: "後で", waiting: "待ち", hold: "保留" };
const laneCycle: ActionLane[] = ["urgent", "later", "waiting", "hold"];
const LANE_SAVE_DELAY_MS = 1_000;
const emptyData: DashboardData = { tickets: [], ticketCatalog: [], ticketFiles: [], ticketRelations: [], actions: [], daily: null, memos: [] };
const ACTION_NOTE_MAX_LENGTH = 10_000;
const MARKDOWN_NOTATION_HELP = "# 見出し、**太字**、*斜体*、~~取消~~、> 引用、`コード`、```コードブロック、--- 区切り線、- 箇条書き、1 番号、[] チェックに対応しています。";

type ActionEditDraft = {
  text: string;
  note: string;
  lane: ActionLane;
  ticketKey: string;
  projectName: string;
  context: string;
  referenceUrl: string;
  reminderOn: string;
  reminderMode: ReminderMode;
  baseRevision: number;
};
type LaneSaveJob = {
  key: string;
  targetId: string;
  affectedIds: string[];
  revision: number;
  savedLane: ActionLane;
  desiredLane: ActionLane;
  lastChangedAt: number;
  timer: number | null;
  saving: boolean;
};
const actionEditDrafts = new Map<string, ActionEditDraft>();
type ActionDropDestination = ActionMoveDestination | { kind: "lane"; lane: ActionLane };


export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [authReady, setAuthReady] = useState(demoMode);

  useEffect(() => {
    if (demoMode || !supabase) return;
    supabase.auth.getSession().then(({ data }) => { setSession(data.session); setAuthReady(true); });
    const { data } = supabase.auth.onAuthStateChange((_event, next) => { setSession(next); setAuthReady(true); });
    return () => data.subscription.unsubscribe();
  }, []);

  if (!repository) return <SetupRequired />;
  if (!authReady) return <AuthLoading />;
  if (!demoMode && !session) return <Login />;
  if (!demoMode && session) return <MembershipGate session={session} />;
  return <Workspace userEmail="demo@local" />;
}

function MembershipGate({ session }: { session: Session }) {
  const [status, setStatus] = useState<"checking" | "allowed" | "denied" | "error">("checking");
  const [initialDashboard] = useState(() => repository!.getDashboard(todayInTokyo()).catch(() => null));

  useEffect(() => {
    let active = true;
    supabase!.from("workspace_memberships").select("role").eq("workspace_id", workspaceId).eq("user_id", session.user.id).maybeSingle()
      .then(({ data, error }) => { if (active) setStatus(error ? "error" : data ? "allowed" : "denied"); });
    return () => { active = false; };
  }, [session.user.id]);

  if (status === "checking") return <AuthLoading detail="利用権限を確認しています" />;
  if (status === "allowed") return <Workspace userEmail={session.user.email || ""} initialDashboard={initialDashboard} />;
  return <main className="centered"><div className="center-card"><div className="logo">D</div><h1>{status === "denied" ? "利用を許可されていません" : "利用権限を確認できません"}</h1><p>{session.user.email}<br/>{status === "denied" ? "このGoogleアカウントはDaily Deskに登録されていません。" : "時間を置いてもう一度お試しください。"}</p><button className="primary large" onClick={() => supabase?.auth.signOut()}>ログアウト</button></div></main>;
}

function AuthLoading({ detail = "セッションを確認しています" }: { detail?: string }) {
  return <main className="auth-transition" aria-live="polite" aria-busy="true">
    <div className="auth-transition-content">
      <img className="auth-transition-logo" src="/icon.svg" alt="" aria-hidden="true" />
      <div className="auth-spinner" aria-hidden="true"><span /></div>
      <strong>認証中…</strong>
      <small>{detail}</small>
    </div>
  </main>;
}

function SetupRequired() {
  return <CenteredMessage title="初期設定が必要です" body=".env.localにSupabase URL、公開anon key、workspace IDを設定してください。実データを扱わないUI確認ではVITE_DEMO_MODE=trueを使えます。" />;
}

function CenteredMessage({ title, body }: { title: string; body: string }) {
  return <main className="centered"><div className="center-card"><div className="logo">D</div><h1>{title}</h1><p>{body}</p></div></main>;
}

function Login() {
  const [error, setError] = useState("");
  async function login() {
    setError("");
    const { error } = await supabase!.auth.signInWithOAuth({ provider: "google", options: { redirectTo: window.location.origin } });
    if (error) setError(error.message);
  }
  return <main className="centered"><div className="center-card"><div className="logo">D</div><p className="eyebrow">Daily Desk / みねやま</p><h1>今日の仕事を、ここから。</h1><p>アクション、Daily、メモをひとつにまとめます。</p><button className="primary large" onClick={login}>Googleでログイン</button>{error && <p className="error">{error}</p>}<p className="safety">許可された仕事用アカウントのみ利用できます。</p></div></main>;
}

function Workspace({ userEmail, initialDashboard }: { userEmail: string; initialDashboard?: Promise<DashboardData | null> }) {
  const initialParams = new URLSearchParams(window.location.search);
  const { embedded, ticketKey: embeddedTicketKey } = getEmbedContext(window.location.search, window.location.pathname);
  const initialView = (() => {
    const value = initialParams.get("view");
    if (embedded) return "actions" as AppView;
    return value && ["tickets", "today", "actions", "memos"].includes(value) ? value as AppView : "today";
  })();
  const [view, setView] = useState<AppView>(initialView);
  const [selectedDate, setSelectedDate] = useState(todayInTokyo());
  const [data, setData] = useState<DashboardData>(emptyData);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [online, setOnline] = useState(navigator.onLine);
  const loadInFlight = useRef<{ date: string; id: symbol; promise: Promise<void> } | null>(null);
  const initialDashboardRef = useRef(initialDashboard);
  const today = todayInTokyo();

  const load = useCallback(() => {
    if (loadInFlight.current?.date === selectedDate) return loadInFlight.current.promise;
    const requestId = Symbol(selectedDate);
    const promise = (async () => {
      try {
        setError("");
        const preloaded = selectedDate === today ? initialDashboardRef.current : undefined;
        initialDashboardRef.current = undefined;
        const preloadedData = preloaded ? await preloaded : null;
        setData(preloadedData || await repository!.getDashboard(selectedDate));
      }
      catch (cause) { setError(cause instanceof Error ? cause.message : "読み込みに失敗しました"); }
      finally {
        if (loadInFlight.current?.id === requestId) loadInFlight.current = null;
        setLoading(false);
      }
    })();
    loadInFlight.current = { date: selectedDate, id: requestId, promise };
    return promise;
  }, [selectedDate]);

  useEffect(() => { load(); return repository!.subscribe(load); }, [load]);

  const reloadNow = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await load();
    } finally {
      window.setTimeout(() => setRefreshing(false), 180);
    }
  }, [load, refreshing]);
  useEffect(() => {
    const on = () => setOnline(true); const off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  const counts = {
    tickets: data.ticketCatalog.filter((ticket) => ticket.status === "active").length,
    actions: data.actions.filter((action) => !action.completed_at && action.lane === "urgent").length,
    memos: data.memos.filter((memo) => memo.pinned).length,
  };
  const viewLabels: Record<AppView, string> = { tickets: "チケット", today: "今日", actions: "アクション", memos: "メモ" };
  function navigate(next: AppView) {
    setView(next);
    if (next === "today") setSelectedDate(today);
    const url = new URL(window.location.href);
    url.searchParams.set("view", next);
    url.searchParams.delete("newAction");
    if (!embedded) url.searchParams.delete("ticket");
    window.history.replaceState({}, "", url);
  }
  function selectDate(next: string) { if (!next || next > today) return; setData((current) => ({ ...current, daily: null })); setSelectedDate(next); }
  return <div className={embedded ? "app-shell embedded-shell" : "app-shell"}>
    {!embedded && <aside className="side-nav">
      <div className="brand"><div className="logo small"><img src="/icon.svg" alt="" aria-hidden="true" /></div><div><strong>Daily Desk</strong><span>みねやま</span></div></div>
      <Navigation view={view} setView={(next) => handleNavigation(next, navigate)} counts={counts} />
      <div className="account"><span className="avatar" title={userEmail}>{userEmail.slice(0, 2).toUpperCase()}</span>{!demoMode && <button className="text-button" onClick={() => supabase?.auth.signOut()} aria-label="ログアウト">↪</button>}</div>
    </aside>}
    <main className={embedded ? "workspace embedded-workspace" : "workspace"}>
      {!embedded && <header className="app-topbar"><span>Daily Desk / {viewLabels[view]}</span><div className="topbar-status"><span className={online ? "sync-state" : "sync-state offline-state"}>{online ? "● SYNC READY" : "● OFFLINE"}</span><button type="button" className={`quiet-reload ${refreshing ? "refreshing" : ""}`} aria-label={refreshing ? "最新状態を再読み込み中" : "最新状態を再読み込み"} title={refreshing ? "再読み込み中…" : "最新状態を再読み込み"} disabled={refreshing} onClick={() => void reloadNow()}>↻</button></div></header>}
      {!online && <div className="offline" role="alert">オフラインです。再接続するまで保存できません。</div>}
      {demoMode && <div className="demo-banner">UI確認用デモ。実在の業務情報は入力しないでください。</div>}
      {error && <div className="error-banner" role="alert">{error}<button onClick={() => void reloadNow()} disabled={refreshing}>再読み込み</button></div>}
      {loading ? <section className="page"><p>読み込み中…</p></section> : <>
        {view === "tickets" && <TicketCatalogPage data={data} online={online} refresh={load} />}
        {view === "today" && <TodayPage data={data} date={selectedDate} today={today} setDate={selectDate} online={online} refresh={load} />}
        {view === "actions" && <ActionsPage data={data} online={online} refresh={load} embedded={embedded} embeddedTicketKey={embeddedTicketKey} />}
        {view === "memos" && <MemosPage data={data} online={online} refresh={load} />}
      </>}
    </main>
    {!embedded && <nav className="bottom-nav"><Navigation view={view} setView={(next) => handleNavigation(next, navigate)} counts={counts} /></nav>}
  </div>;
}

function Navigation({ view, setView, counts }: { view: AppView; setView: (view: AppView) => void; counts: Record<string, number> }) {
  const items: { id: AppView; icon: string; label: string; count?: number }[] = [
    { id: "tickets", icon: "▤", label: "チケット", count: counts.tickets },
    { id: "today", icon: "○", label: "今日" }, { id: "actions", icon: "✓", label: "アクション", count: counts.actions },
    { id: "memos", icon: "≋", label: "メモ", count: counts.memos },
  ];
  return <div className="nav-items">{items.map((item) => <button key={item.id} className={view === item.id ? "active" : ""} onClick={() => setView(item.id)}><span className="nav-icon">{item.icon}</span><span>{item.label}</span>{item.count !== undefined && item.count > 0 && <em>{item.count}</em>}</button>)}</div>;
}

function PageHeader({ eyebrow, title, children }: { eyebrow: string; title: string; children?: React.ReactNode }) {
  return <header className="page-header"><div><p className="eyebrow">{eyebrow}</p><h1>{title}</h1></div>{children}</header>;
}

function nextActionLane(lane: ActionLane): ActionLane {
  return laneCycle[(laneCycle.indexOf(lane) + 1) % laneCycle.length];
}

function LaneCycleButton({ action, disabled, onCycle }: { action: Action; disabled: boolean; onCycle: (action: Action) => Promise<void> }) {
  const [previewLane, setPreviewLane] = useState<ActionLane | null>(null);
  const displayedLane = previewLane || action.lane;
  useEffect(() => { setPreviewLane(null); }, [action.lane]);
  const previewNextLane = () => setPreviewLane(nextActionLane(action.lane));
  return <button
    type="button"
    className={`lane-chip lane-chip-button lane-chip-${displayedLane}`}
    disabled={disabled}
    onPointerDown={previewNextLane}
    onPointerCancel={() => setPreviewLane(null)}
    onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") previewNextLane(); }}
    onClick={(event) => { event.stopPropagation(); if (!previewLane) previewNextLane(); void onCycle(action); }}
    aria-label={`急ぎ度を変更: 現在${laneLabels[displayedLane]}`}
    title="クリックで急ぎ度を変更"
  ><span className="lane-label-desktop">{laneLabels[displayedLane]}</span><span className="lane-label-mobile">{laneLabels[displayedLane]}</span></button>;
}

function useMobileBodyScrollLock(active: boolean) {
  useEffect(() => {
    if (!active || !window.matchMedia("(max-width: 760px)").matches) return;
    const body = document.body;
    const scrollY = window.scrollY;
    const previous = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";
    return () => {
      body.style.position = previous.position;
      body.style.top = previous.top;
      body.style.left = previous.left;
      body.style.right = previous.right;
      body.style.width = previous.width;
      body.style.overflow = previous.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [active]);
}

function useSwipeRightToClose(onClose: () => void, disabled = false) {
  const startRef = useRef<{ x: number; y: number } | null>(null);

  function onTouchStart(event: React.TouchEvent<HTMLElement>) {
    if (disabled) { startRef.current = null; return; }
    const target = event.target as HTMLElement;
    if (target.closest("input, textarea, select, button, a, [contenteditable='true']")) {
      startRef.current = null;
      return;
    }
    const touch = event.touches[0];
    startRef.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
  }

  function onTouchEnd(event: React.TouchEvent<HTMLElement>) {
    const start = startRef.current;
    startRef.current = null;
    if (!start || disabled) return;
    const touch = event.changedTouches[0];
    if (!touch) return;
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    if (dx >= 72 && Math.abs(dx) > Math.abs(dy) * 1.35) onClose();
  }

  return {
    onTouchStart,
    onTouchEnd,
    onTouchCancel: () => { startRef.current = null; },
  };
}

function useCancelMoveOnOutside(movingId: string | null, cancel: () => void) {
  const lastTapRef = useRef(0);

  useEffect(() => {
    if (!movingId) {
      lastTapRef.current = 0;
      return;
    }

    const isInsideMoveArea = (target: EventTarget | null) =>
      target instanceof Element && Boolean(target.closest(".action-item, .move-status"));

    const onDoubleClick = (event: MouseEvent) => {
      if (!isInsideMoveArea(event.target)) cancel();
    };

    const onPointerUp = (event: PointerEvent) => {
      if (event.pointerType !== "touch" || isInsideMoveArea(event.target)) return;
      const now = Date.now();
      if (now - lastTapRef.current <= 420) {
        lastTapRef.current = 0;
        cancel();
        return;
      }
      lastTapRef.current = now;
    };

    document.addEventListener("dblclick", onDoubleClick, true);
    document.addEventListener("pointerup", onPointerUp, true);
    return () => {
      document.removeEventListener("dblclick", onDoubleClick, true);
      document.removeEventListener("pointerup", onPointerUp, true);
    };
  }, [movingId, cancel]);
}

function TodayPage({ data, date, today, setDate, online, refresh }: PageProps & { date: string; today: string; setDate: (date: string) => void }) {
  const [note, setNote] = useState(data.daily?.markdown || "");
  const [saving, setSaving] = useState(false);
  const [noteSaved, setNoteSaved] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [subActionParent, setSubActionParent] = useState<Action | null>(null);
  const [movingId, setMovingId] = useState<string | null>(null);
  const [detailDirty, setDetailDirty] = useState(false);
  const [todayOrderIds, setTodayOrderIds] = useState<string[]>([]);
  const [laneOverrides, setLaneOverrides] = useState<Record<string, ActionLane>>({});
  const [pendingLaneIds, setPendingLaneIds] = useState<Set<string>>(new Set());
  const laneSaveJobs = useRef<Map<string, LaneSaveJob>>(new Map());
  const laneJobKeysByActionId = useRef<Map<string, string>>(new Map());
  const laneSaveUnmounted = useRef(false);
  const [todayOverrides, setTodayOverrides] = useState<Record<string, boolean>>({});
  const [pendingTodayIds, setPendingTodayIds] = useState<Set<string>>(new Set());
  const pendingTodayActions = useRef<Set<string>>(new Set());
  const datePickerRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => { setNote(data.daily?.markdown || ""); setNoteSaved(false); }, [data.daily]);
  const noteDirty = note !== (data.daily?.markdown || "");
  const isToday = date === today;
  const actualDailyActionIds = new Set(actionsForDaily(data.actions, date, today).map((action) => action.id));
  const displayedDailyActionIds = new Set(actualDailyActionIds);
  Object.entries(todayOverrides).forEach(([id, included]) => { if (included) displayedDailyActionIds.add(id); else displayedDailyActionIds.delete(id); });
  useEffect(() => {
    setTodayOverrides((current) => {
      const next = { ...current };
      let changed = false;
      for (const [id, included] of Object.entries(current)) {
        if (actualDailyActionIds.has(id) !== included) continue;
        delete next[id];
        changed = true;
      }
      return changed ? next : current;
    });
  }, [data.actions, date]);
  const dailyActions = data.actions
    .filter((action) => displayedDailyActionIds.has(action.id))
    .map((action) => laneOverrides[action.id] ? { ...action, lane: laneOverrides[action.id] } : action);
  useEffect(() => {
    setLaneOverrides((current) => {
      const next = { ...current };
      let changed = false;
      for (const [id, lane] of Object.entries(current)) {
        if (data.actions.find((action) => action.id === id)?.lane !== lane) continue;
        delete next[id];
        changed = true;
      }
      return changed ? next : current;
    });
    for (const [key, job] of laneSaveJobs.current) {
      if (job.saving || job.timer !== null || job.desiredLane !== job.savedLane) continue;
      const stored = data.actions.find((action) => action.id === job.targetId);
      if (!stored || stored.revision < job.revision || stored.lane !== job.savedLane) continue;
      laneSaveJobs.current.delete(key);
      job.affectedIds.forEach((id) => laneJobKeysByActionId.current.delete(id));
    }
  }, [data.actions]);
  useEffect(() => {
    laneSaveUnmounted.current = false;
    return () => {
      laneSaveUnmounted.current = true;
      for (const job of laneSaveJobs.current.values()) {
        if (job.timer !== null) window.clearTimeout(job.timer);
        job.timer = null;
        if (!job.saving && job.desiredLane !== job.savedLane) void persistLaneJob(job.key);
      }
    };
  }, []);
  const pickerCandidates = candidatesForToday(data.actions, today);
  useEffect(() => {
    const currentIds = dailyActions.map((action) => action.id);
    setTodayOrderIds((previous) => {
      const next = mergeNewActionIdsAtTop(previous, currentIds);
      return next.length === previous.length && next.every((id, index) => id === previous[index]) ? previous : next;
    });
  }, [date, data.actions]);
  const displayedDailyActions = useMemo(() => {
    const base = [...dailyActions].sort((a, b) => {
      const aIndex = todayOrderIds.indexOf(a.id);
      const bIndex = todayOrderIds.indexOf(b.id);
      if (aIndex < 0 && bIndex < 0) return a.sort_order - b.sort_order;
      if (aIndex < 0) return 1;
      if (bIndex < 0) return -1;
      return aIndex - bIndex;
    });
    const visibleIds = new Set(base.map((action) => action.id));
    const indexById = new Map(base.map((action, index) => [action.id, index]));
    const grouped = new Set<string>();
    const result: Action[] = [];

    for (const action of base) {
      if (grouped.has(action.id)) continue;
      if (action.parent_id && visibleIds.has(action.parent_id)) continue;

      result.push(action);
      grouped.add(action.id);

      const children = base
        .filter((child) => child.parent_id === action.id && visibleIds.has(child.id))
        .sort((a, b) => (indexById.get(a.id) ?? 0) - (indexById.get(b.id) ?? 0));
      for (const child of children) {
        result.push(child);
        grouped.add(child.id);
      }
    }

    for (const action of base) {
      if (!grouped.has(action.id)) result.push(action);
    }
    return result;
  }, [dailyActions, todayOrderIds]);
  const selectedAction = selectedId ? data.actions.find((action) => action.id === selectedId) || null : null;
  useEffect(() => {
    if (!movingId) return;
    const cancelMove = (event: KeyboardEvent) => { if (event.key === "Escape") setMovingId(null); };
    window.addEventListener("keydown", cancelMove);
    return () => window.removeEventListener("keydown", cancelMove);
  }, [movingId]);
  function highlight(id: string) { setHighlightedId(id); window.setTimeout(() => setHighlightedId((current) => current === id ? null : current), 1800); }
  function closeActionDetail() { if (detailDirty && !window.confirm("未保存の入力を破棄して閉じますか？")) return; setSelectedId(null); setDetailDirty(false); }
  const actionDetailSwipe = useSwipeRightToClose(closeActionDetail);
  useMobileBodyScrollLock(Boolean(isToday && selectedAction && !movingId));
  function selectAction(action: Action) { if (detailDirty && !window.confirm("未保存のアクションメモを破棄しますか？")) return; setSelectedId((current) => current === action.id ? null : action.id); setDetailDirty(false); }
  function beginSubAction(action: Action) {
    if (action.parent_id) return;
    if (detailDirty && !window.confirm("未保存のアクションメモを破棄してサブアクションを追加しますか？")) return;
    setSelectedId(null);
    setDetailDirty(false);
    setSubActionParent(action);
  }
  function startMove(action: Action) { if (detailDirty && !window.confirm("未保存のアクションメモを破棄して移動しますか？")) return; const root = action.parent_id ? data.actions.find((item) => item.id === action.parent_id) || action : action; setSelectedId(null); setDetailDirty(false); setMovingId(root.id); }
  async function moveAction(action: Action, destination: ActionDropDestination) {
    try {
      const root = action.parent_id ? data.actions.find((item) => item.id === action.parent_id) || action : action;
      if (destination.kind === "lane") return;
      if (destination.kind === "child") {
        await repository!.moveAction(root, destination);
      } else {
        const destinationAction = displayedDailyActions.find((item) => item.id === destination.target_id);
        const targetRoot = destinationAction?.parent_id ? displayedDailyActions.find((item) => item.id === destinationAction.parent_id) || destinationAction : destinationAction;
        if (!targetRoot || targetRoot.id === root.id) return;
        const groupIds = displayedDailyActions.filter((item) => item.id === root.id || item.parent_id === root.id).map((item) => item.id);
        const targetGroupIds = displayedDailyActions.filter((item) => item.id === targetRoot.id || item.parent_id === targetRoot.id).map((item) => item.id);
        const groupIdSet = new Set(groupIds);
        const ids = displayedDailyActions.map((item) => item.id).filter((id) => !groupIdSet.has(id));
        const targetAnchorId = destination.kind === "before" ? targetGroupIds[0] : targetGroupIds[targetGroupIds.length - 1];
        const targetIndex = ids.indexOf(targetAnchorId);
        if (targetIndex < 0) return;
        ids.splice(targetIndex + (destination.kind === "after" ? 1 : 0), 0, ...groupIds);
        setTodayOrderIds(ids);
        await repository!.reorderActions(ids);
      }
      await refresh();
      setMovingId(null);
      setSelectedId(null);
    } catch (cause) {
      setMovingId(null);
      await refresh();
      window.alert(cause instanceof Error ? cause.message : "アクションを移動できませんでした");
    }
  }
  async function removeFromToday(action: Action) {
    await toggleRelatedActionToday(action, false);
    if (selectedId === action.id) setSelectedId(null);
    if (movingId === action.id) setMovingId(null);
  }
  async function toggleRelatedActionToday(action: Action, nextIncluded?: boolean) {
    if (!online || action.completed_at || pendingTodayActions.current.has(action.id)) return;
    const alreadyToday = displayedDailyActionIds.has(action.id);
    const include = nextIncluded ?? !alreadyToday;
    if (include === alreadyToday) return;
    pendingTodayActions.current.add(action.id);
    setPendingTodayIds((current) => new Set(current).add(action.id));
    setTodayOverrides((current) => ({ ...current, [action.id]: include }));
    try {
      await repository!.updateAction(action.id, action.revision, {
        today_override_on: today,
        today_override_visible: include,
      });
      await refresh();
    } catch {
      await refresh();
      setTodayOverrides((current) => { const next = { ...current }; delete next[action.id]; return next; });
      window.alert(include ? "今日に追加できませんでした。通信状態を確認して、もう一度お試しください。" : "今日から外せませんでした。通信状態を確認して、もう一度お試しください。");
    } finally {
      pendingTodayActions.current.delete(action.id);
      setPendingTodayIds((current) => { const next = new Set(current); next.delete(action.id); return next; });
    }
  }
  function clearLaneJobPending(job: LaneSaveJob) {
    setPendingLaneIds((current) => {
      const next = new Set(current);
      job.affectedIds.forEach((id) => next.delete(id));
      return next;
    });
  }
  function removeLaneSaveJob(job: LaneSaveJob) {
    if (job.timer !== null) window.clearTimeout(job.timer);
    job.timer = null;
    laneSaveJobs.current.delete(job.key);
    job.affectedIds.forEach((id) => {
      if (laneJobKeysByActionId.current.get(id) === job.key) laneJobKeysByActionId.current.delete(id);
    });
  }
  function scheduleLaneSave(job: LaneSaveJob, delay = LANE_SAVE_DELAY_MS) {
    if (job.timer !== null) window.clearTimeout(job.timer);
    job.timer = window.setTimeout(() => { void persistLaneJob(job.key); }, delay);
  }
  async function persistLaneJob(key: string) {
    const job = laneSaveJobs.current.get(key);
    if (!job) return;
    job.timer = null;
    if (job.saving) return;
    if (job.desiredLane === job.savedLane) {
      clearLaneJobPending(job);
      return;
    }

    job.saving = true;
    const savingLane = job.desiredLane;
    try {
      job.revision = await repository!.changeActionLane({ id: job.targetId, revision: job.revision }, savingLane);
      job.savedLane = savingLane;
      await refresh();
    } catch {
      try { await refresh(); } catch { /* The lane error below remains the actionable failure. */ }
      removeLaneSaveJob(job);
      if (!laneSaveUnmounted.current) {
        clearLaneJobPending(job);
        setLaneOverrides((current) => {
          const next = { ...current };
          job.affectedIds.forEach((id) => delete next[id]);
          return next;
        });
        window.alert("優先度を変更できませんでした。通信状態を確認して、もう一度お試しください。");
      }
      return;
    } finally {
      job.saving = false;
    }

    if (job.desiredLane === job.savedLane) {
      if (job.timer !== null) window.clearTimeout(job.timer);
      job.timer = null;
      if (!laneSaveUnmounted.current) clearLaneJobPending(job);
      return;
    }
    if (laneSaveUnmounted.current) {
      void persistLaneJob(job.key);
      return;
    }
    if (job.timer === null) {
      const remaining = Math.max(0, LANE_SAVE_DELAY_MS - (Date.now() - job.lastChangedAt));
      scheduleLaneSave(job, remaining);
    }
  }
  async function cycleTodayLane(action: Action) {
    if (!online) return;
    const existingKey = laneJobKeysByActionId.current.get(action.id);
    let job = existingKey ? laneSaveJobs.current.get(existingKey) : undefined;
    if (!job) {
      const openActions = data.actions.filter((item) => !item.completed_at);
      const storedAction = openActions.find((item) => item.id === action.id);
      if (!storedAction) return;
      const affectedIds = [storedAction.id];
      job = {
        key: storedAction.id,
        targetId: storedAction.id,
        affectedIds,
        revision: storedAction.revision,
        savedLane: storedAction.lane,
        desiredLane: storedAction.lane,
        lastChangedAt: Date.now(),
        timer: null,
        saving: false,
      };
      laneSaveJobs.current.set(job.key, job);
      affectedIds.forEach((id) => laneJobKeysByActionId.current.set(id, job!.key));
    }

    job.desiredLane = nextActionLane(job.desiredLane);
    job.lastChangedAt = Date.now();
    setPendingLaneIds((current) => new Set([...current, ...job!.affectedIds]));
    setLaneOverrides((current) => {
      const next = { ...current };
      job!.affectedIds.forEach((id) => { next[id] = job!.desiredLane; });
      return next;
    });

    if (!job.saving && job.desiredLane === job.savedLane) {
      if (job.timer !== null) window.clearTimeout(job.timer);
      job.timer = null;
      clearLaneJobPending(job);
      return;
    }
    scheduleLaneSave(job);
  }
  async function resetTodayOrder() {
    const laneRank: Record<ActionLane, number> = { urgent: 0, later: 1, waiting: 2, hold: 3 };
    const ordered = [...dailyActions].sort((a, b) => laneRank[effectiveActionLane(a, dailyActions)] - laneRank[effectiveActionLane(b, dailyActions)] || a.sort_order - b.sort_order);
    const orderedIds = ordered.map((action) => action.id);
    setTodayOrderIds(orderedIds);
    await repository!.reorderActions(orderedIds);
    setMovingId(null);
    await refresh();
  }
  async function saveNote() {
    if (!online || saving || !noteDirty) return;
    setSaving(true);
    setNoteSaved(false);
    try {
      await repository!.saveDaily(date, note, data.daily?.revision);
      await refresh();
      setNoteSaved(true);
      window.setTimeout(() => setNoteSaved(false), 1600);
    } finally {
      setSaving(false);
    }
  }
  useCancelMoveOnOutside(movingId, () => setMovingId(null));
  function openDatePicker() {
    const input = datePickerRef.current;
    if (!input) return;
    const picker = input as HTMLInputElement & { showPicker?: () => void };
    try { if (picker.showPicker) picker.showPicker(); else input.click(); }
    catch { input.click(); }
  }
  return <section className="page"><PageHeader eyebrow={displayDate(date)} title={isToday ? "今日のデスク" : "Dailyアーカイブ"}><div className="date-toolbar"><button className="date-step" aria-label="前日" onClick={() => setDate(shiftTokyoDate(date, -1))}>←</button><button type="button" className="date-display-button" aria-label="日付を選択" onClick={openDatePicker}>{date.replaceAll("-", "/")}</button><button className="date-step" aria-label="翌日" disabled={isToday} onClick={() => setDate(shiftTokyoDate(date, 1))}>→</button><input ref={datePickerRef} className="native-date-picker" aria-label="Dailyの日付" type="date" value={date} max={today} onChange={(event) => setDate(event.target.value)}/>{!isToday && <button className="today-button" onClick={() => setDate(today)}>今日へ</button>}</div></PageHeader>
    <div className="today-grid"><section className="panel today-actions-panel"><div className="panel-heading"><div><p className="eyebrow">{isToday ? "フォーカス" : "記録"}</p><h2>{isToday ? "今日のアクション" : "この日に完了"}</h2></div><div className="panel-tools">{isToday && <><button className="today-order-reset today-order-reset-desktop" type="button" disabled={!online || displayedDailyActions.length < 2} onClick={() => void resetTodayOrder()}>↻ 急ぎ度順に戻す</button><button className="pick-action-button" disabled={!online} onClick={() => setPickerOpen(true)}>アクションから選ぶ</button><button className="inline-add-button" type="button" aria-label="今日のアクションを追加" title="今日のアクションを追加" disabled={!online} onClick={() => setCreating(true)}>＋</button></>}<span className="count-pill">{displayedDailyActions.length}</span></div></div>
      {isToday && <div className="today-mobile-tools"><button className="today-order-reset" type="button" disabled={!online || displayedDailyActions.length < 2} onClick={() => void resetTodayOrder()}>↻ 急ぎ度順に戻す</button></div>}
      {isToday && movingId && <><MoveStatus action={data.actions.find((action) => action.id === movingId)} cancel={() => setMovingId(null)} /><button type="button" className="mobile-move-cancel" onClick={() => setMovingId(null)}>移動をキャンセル</button></>}
      <ActionList actions={displayedDailyActions} allActions={data.actions} tickets={data.tickets} ticketCatalog={data.ticketCatalog} online={online} refresh={refresh} compact readOnly={!isToday} flatReorder={isToday} showLaneBadge={isToday} selectedId={isToday ? selectedId : null} movingId={isToday ? movingId : null} highlightedId={highlightedId} pendingLaneIds={pendingLaneIds} pendingTodayIds={pendingTodayIds} onSelect={isToday ? selectAction : undefined} onStartMove={isToday ? startMove : undefined} onMoveAction={isToday ? moveAction : undefined} onRemoveFromToday={isToday ? removeFromToday : undefined} onCycleLane={isToday ? cycleTodayLane : undefined} onAddSubAction={isToday ? beginSubAction : undefined} onDetailDirtyChange={setDetailDirty} />
    </section>
    <section className="panel daily-panel"><div className="panel-heading"><div><p className="eyebrow">DAILY</p><h2>{isToday ? "今日のメモ" : "この日のメモ"}</h2></div><div className="daily-heading-actions"><span className={noteSaved ? "daily-save-state saved" : "daily-save-state"}>{saving ? "保存中…" : noteSaved ? "保存しました" : noteDirty ? "未保存" : data.daily ? `v${data.daily.revision}` : "新規"}</span>{(noteDirty || saving) && !noteSaved && <button className="primary daily-save-button" disabled={!online || saving || !noteDirty} onClick={saveNote}>{saving ? "保存中…" : "保存"}</button>}</div></div><textarea className="daily-editor" value={note} onChange={(e) => { setNote(e.target.value); setNoteSaved(false); }} onKeyDown={(event) => { if ((event.ctrlKey || event.metaKey) && event.key === "Enter") { event.preventDefault(); void saveNote(); } }} placeholder="決めたこと、気づいたこと、明日へ残すこと…"/></section></div>
    {pickerOpen && <ActionPicker actions={pickerCandidates} tickets={data.tickets} date={today} close={() => setPickerOpen(false)} refresh={refresh} />}
    {creating && <ActionCreator actions={data.actions.filter((action) => !action.completed_at)} tickets={data.tickets} catalog={data.ticketCatalog} online={online} fixedDate={today} close={() => setCreating(false)} refresh={refresh} onCreated={highlight} />}
    {subActionParent && <ActionCreator actions={data.actions.filter((action) => !action.completed_at)} tickets={data.tickets} catalog={data.ticketCatalog} online={online} parent={subActionParent} close={() => setSubActionParent(null)} refresh={refresh} onCreated={highlight} />}
    {isToday && selectedAction && !movingId && <div className="modal-backdrop action-detail-modal" {...actionDetailSwipe} onMouseDown={(event) => { if (event.target === event.currentTarget) closeActionDetail(); }}><div className="action-detail-dialog"><header><div><p className="eyebrow">ACTION</p><h2>アクション詳細</h2></div><button type="button" className="icon-button" aria-label="閉じる" onClick={closeActionDetail}>×</button></header><ActionDetails action={selectedAction} allActions={data.actions} ticketCatalog={data.ticketCatalog} online={online} refresh={refresh} onSaved={() => { setSelectedId(null); setDetailDirty(false); }} onAddSubAction={!selectedAction.parent_id ? beginSubAction : undefined} onDirtyChange={setDetailDirty} todayActionIds={displayedDailyActionIds} pendingTodayIds={pendingTodayIds} onToggleRelatedToday={toggleRelatedActionToday} keepOnTodayDate={today} /></div></div>}
  </section>;
}

function ActionPicker({ actions, tickets: _tickets, date, close, refresh }: { actions: Action[]; tickets: Ticket[]; date: string; close: () => void; refresh: () => Promise<void> }) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const visible = actions.filter((action) => `${action.text} ${action.project_name} ${action.context}`.toLowerCase().includes(query.toLowerCase()));
  function toggle(id: string) { setSelected((current) => { const next = new Set(current); if (next.has(id)) next.delete(id); else next.add(id); return next; }); }
  async function apply(e: FormEvent) { e.preventDefault(); const targets = actions.filter((action) => selected.has(action.id)); if (!targets.length) return; await repository!.scheduleActionsForDate(targets, date); await refresh(); close(); }
  return <div className="modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}><form className="picker-card" onSubmit={apply}><header><div><p className="eyebrow">OPEN ACTIONS</p><h2>今日やるアクションを選ぶ</h2></div><button type="button" className="icon-button" onClick={close} aria-label="閉じる">×</button></header><input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="アクション、プロジェクト、文脈で検索" autoFocus/><div className="picker-list">{visible.map((action) => <label className="picker-row" key={action.id}><input type="checkbox" checked={selected.has(action.id)} onChange={() => toggle(action.id)}/><span><strong>{action.text}</strong><small>{[action.project_name, action.context, action.reminder_on && `${action.reminder_on} · ${action.reminder_mode === "once" ? "その日だけ" : "完了まで毎日"}`].filter(Boolean).join(" · ") || "文脈未設定"}</small></span><span className={`lane-chip lane-chip-${action.lane}`}>{laneLabels[action.lane]}</span></label>)}{!visible.length && <Empty text="選択できる未完了アクションはありません。"/>}</div><footer><span>{selected.size}件を選択</span><div><button type="button" className="secondary" onClick={close}>キャンセル</button><button className="primary" disabled={!selected.size}>今日に反映</button></div></footer></form></div>;
}

interface PageProps { data: DashboardData; online: boolean; refresh: () => Promise<void>; }

function ActionList({ actions, allActions = actions, tickets, ticketCatalog = [], online, refresh, compact = false, readOnly = false, flatReorder = false, crossLaneReorder = false, ticketBlockReorder = false, lane, showLaneBadge = false, selectedId, movingId, highlightedId, pendingLaneIds, pendingTodayIds, onSelect, onStartMove, onMoveAction, onRemoveFromToday, onAddToToday, todayActionIds, onCycleLane }: { actions: Action[]; allActions?: Action[]; tickets: Ticket[]; ticketCatalog?: TicketCatalog[]; online: boolean; refresh: () => Promise<void>; compact?: boolean; readOnly?: boolean; flatReorder?: boolean; crossLaneReorder?: boolean; ticketBlockReorder?: boolean; lane?: ActionLane; showLaneBadge?: boolean; selectedId?: string | null; movingId?: string | null; highlightedId?: string | null; pendingLaneIds?: Set<string>; pendingTodayIds?: Set<string>; onSelect?: (action: Action) => void; onStartMove?: (action: Action) => void; onMoveAction?: (action: Action, destination: ActionDropDestination) => Promise<void>; onRemoveFromToday?: (action: Action) => Promise<void>; onAddToToday?: (action: Action) => Promise<void>; todayActionIds?: Set<string>; onCycleLane?: (action: Action) => Promise<void>; onAddSubAction?: (action: Action) => void; onDetailDirtyChange?: (dirty: boolean) => void }) {
  const byId = new Map(tickets.map((ticket) => [ticket.id, ticket]));
  const byKey = new Map(ticketCatalog.map((ticket) => [ticket.ticket_key, ticket]));
  const visibleIds = new Set(actions.map((action) => action.id));
  const suppressClick = useRef(false);
  const singleClickTimer = useRef<number | null>(null);
  const longPressTimer = useRef<number | null>(null);
  const [pendingCompletion, setPendingCompletion] = useState<Record<string, boolean>>({});
  const [leavingCompletionIds, setLeavingCompletionIds] = useState<Set<string>>(new Set());
  const [hiddenCompletionIds, setHiddenCompletionIds] = useState<Set<string>>(new Set());
  const touchDrag = useRef<{ action: Action; active: boolean; destination: ActionMoveDestination | null; pointerId: number | null }>({ action: actions[0], active: false, destination: null, pointerId: null });
  const smoothTouchCleanup = useRef<(() => void) | null>(null);
  const smoothPointerCleanup = useRef<(() => void) | null>(null);
  const smoothDrag = useRef<{
    source: Action;
    touchId: number;
    active: boolean;
    startX: number;
    startY: number;
    x: number;
    y: number;
    timer: number;
    destination: ActionDropDestination | null;
    ghost: HTMLElement | null;
    scrollFrame: number | null;
    previewFrame: number | null;
    blocks: Map<string, { id: string; action: Action; items: HTMLElement[]; rect: DOMRect; list: HTMLElement }>;
    orders: Map<HTMLElement, string[]>;
  } | null>(null);
  const swipeRemove = useRef<{ action: Action; pointerId: number | null; startX: number; startY: number; armed: boolean }>({ action: actions[0], pointerId: null, startX: 0, startY: 0, armed: false });
  function clearTouchDropPreview() {
    document.querySelectorAll(".action-item.touch-drop-before, .action-item.touch-drop-after, .action-item.touch-drop-child, .action-item.touch-drop-child-blocked")
      .forEach((element) => element.classList.remove("touch-drop-before", "touch-drop-after", "touch-drop-child", "touch-drop-child-blocked"));
  }
  useEffect(() => () => {
    if (singleClickTimer.current !== null) window.clearTimeout(singleClickTimer.current);
    if (longPressTimer.current !== null) window.clearTimeout(longPressTimer.current);
    smoothTouchCleanup.current?.();
    smoothPointerCleanup.current?.();
    if (smoothDrag.current?.active) clearSmoothActionDrag(smoothDrag.current);
    else smoothDrag.current = null;
    clearTouchDropPreview();
  }, []);
  const moving = movingId ? allActions.find((action) => action.id === movingId) : undefined;
  const movingRoots = moving && ticketBlockReorder ? actionTicketBlockRoots(allActions, moving) : moving ? [moving] : [];
  const movingRootIds = new Set(movingRoots.map((action) => action.id));
  const movingActionIds = new Set([...movingRootIds, ...allActions.filter((action) => action.parent_id && movingRootIds.has(action.parent_id)).map((action) => action.id)]);
  const movingHasChildren = Boolean(moving && (movingRoots.length > 1 || allActions.some((action) => action.parent_id && movingRootIds.has(action.parent_id))));
  const reorderTargets = moving ? actions.filter((action) => {
    if (movingActionIds.has(action.id)) return false;
    if (ticketBlockReorder && !action.parent_id && rootAction(action).id !== action.id) return false;
    return flatReorder || (crossLaneReorder && !moving.parent_id && !action.parent_id) || (action.parent_id === moving.parent_id && Boolean(action.parent_id));
  }) : [];
  const lastReorderTarget = reorderTargets.at(-1);

  function beginTouchDrag(action: Action, event: React.PointerEvent<HTMLButtonElement>) {
    if (event.pointerType !== "mouse" && onRemoveFromToday && !readOnly) {
      swipeRemove.current = { action, pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, armed: false };
      event.currentTarget.setPointerCapture?.(event.pointerId);
    }
    if (event.pointerType !== "mouse" || !online || !onMoveAction || readOnly || smoothDrag.current) return;
    const source = rootAction(action);
    const drag = { source, touchId: event.pointerId, active: false, startX: event.clientX, startY: event.clientY, x: event.clientX, y: event.clientY, timer: 0, destination: null, ghost: null, scrollFrame: null, previewFrame: null, blocks: new Map(), orders: new Map() };
    smoothDrag.current = drag;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const cleanup = () => {
      document.removeEventListener("pointermove", onMove, true);
      document.removeEventListener("pointerup", onEnd, true);
      document.removeEventListener("pointercancel", onCancel, true);
      document.removeEventListener("visibilitychange", onVisibilityChange, true);
      window.removeEventListener("blur", onCancel);
      if (smoothPointerCleanup.current === cleanup) smoothPointerCleanup.current = null;
    };
    const onMove = (nativeEvent: PointerEvent) => {
      if (nativeEvent.pointerId !== drag.touchId || smoothDrag.current !== drag) return;
      drag.x = nativeEvent.clientX;
      drag.y = nativeEvent.clientY;
      if (!drag.active && Math.hypot(drag.x - drag.startX, drag.y - drag.startY) > 4) activateSmoothActionDrag(drag);
      if (!drag.active) return;
      nativeEvent.preventDefault();
      queueSmoothActionPreview(drag);
    };
    const onEnd = (nativeEvent: PointerEvent) => {
      if (nativeEvent.pointerId !== drag.touchId || smoothDrag.current !== drag) return;
      cleanup();
      if (!drag.active) { smoothDrag.current = null; return; }
      nativeEvent.preventDefault();
      finishSmoothActionDrag(drag);
    };
    const onCancel = () => {
      if (smoothDrag.current !== drag) return;
      cleanup();
      if (drag.active) clearSmoothActionDrag(drag);
      else smoothDrag.current = null;
    };
    const onVisibilityChange = () => { if (document.visibilityState === "hidden") onCancel(); };
    smoothPointerCleanup.current?.();
    smoothPointerCleanup.current = cleanup;
    document.addEventListener("pointermove", onMove, { passive: false, capture: true });
    document.addEventListener("pointerup", onEnd, { passive: false, capture: true });
    document.addEventListener("pointercancel", onCancel, { capture: true });
    document.addEventListener("visibilitychange", onVisibilityChange, { capture: true });
    window.addEventListener("blur", onCancel);
  }

  function updateTouchDrag(event: React.PointerEvent<HTMLButtonElement>) {
    const pointerDrag = smoothDrag.current;
    if (event.pointerType === "mouse" && pointerDrag?.touchId === event.pointerId) {
      pointerDrag.x = event.clientX;
      pointerDrag.y = event.clientY;
      if (!pointerDrag.active && Math.hypot(pointerDrag.x - pointerDrag.startX, pointerDrag.y - pointerDrag.startY) > 4) activateSmoothActionDrag(pointerDrag);
      if (pointerDrag.active) {
        event.preventDefault();
        queueSmoothActionPreview(pointerDrag);
      }
      return;
    }
    if (smoothDrag.current?.active) { event.preventDefault(); return; }
    if (swipeRemove.current.pointerId === event.pointerId && onRemoveFromToday && !readOnly) {
      const dx = event.clientX - swipeRemove.current.startX;
      const dy = event.clientY - swipeRemove.current.startY;
      if (dx < -10 && Math.abs(dx) > Math.abs(dy) * 1.15) {
        const item = event.currentTarget.closest<HTMLElement>(".action-item");
        const offset = Math.max(-104, Math.min(0, dx));
        swipeRemove.current.armed = dx < -72;
        if (longPressTimer.current !== null) {
          window.clearTimeout(longPressTimer.current);
          longPressTimer.current = null;
        }
        suppressClick.current = true;
        event.currentTarget.setPointerCapture?.(event.pointerId);
        item?.style.setProperty("--swipe-remove-offset", `${offset}px`);
        item?.classList.add("swipe-removing");
        item?.classList.toggle("swipe-remove-armed", swipeRemove.current.armed);
        event.preventDefault();
      }
    }
    if (!touchDrag.current.active || touchDrag.current.pointerId !== event.pointerId) return;
    event.preventDefault();
    clearTouchDropPreview();
    touchDrag.current.destination = null;
    const source = touchDrag.current.action;
    const element = document.elementFromPoint(event.clientX, event.clientY)?.closest<HTMLElement>(".action-item[data-action-id]");
    if (!element) return;
    const target = allActions.find((item) => item.id === element.dataset.actionId);
    if (!target || target.id === source.id || target.completed_at) return;
    const rect = element.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (event.clientY - rect.top) / Math.max(rect.height, 1)));
    const sourceHasChildren = allActions.some((item) => item.parent_id === source.id);
    if (ratio > 0.28 && ratio < 0.72 && !source.parent_id && source.id !== target.id && (flatReorder || !target.parent_id)) {
      if (sourceHasChildren) {
        element.classList.add("touch-drop-child-blocked");
        return;
      }
      touchDrag.current.destination = { kind: "child", target_id: target.id, target_revision: target.revision };
      element.classList.add("touch-drop-child");
      return;
    }
    if (flatReorder || (crossLaneReorder && !source.parent_id && !target.parent_id)) {
      const kind = ratio < 0.5 ? "before" : "after";
      touchDrag.current.destination = { kind, target_id: target.id, target_revision: target.revision };
      element.classList.add(kind === "before" ? "touch-drop-before" : "touch-drop-after");
      return;
    }
    if (ratio > 0.28 && ratio < 0.72 && !sourceHasChildren && !target.parent_id && source.parent_id !== target.id) {
      touchDrag.current.destination = { kind: "child", target_id: target.id, target_revision: target.revision };
      element.classList.add("touch-drop-child");
      return;
    }
    if (target.parent_id === source.parent_id && Boolean(target.parent_id)) {
      const kind = ratio < 0.5 ? "before" : "after";
      touchDrag.current.destination = { kind, target_id: target.id, target_revision: target.revision };
      element.classList.add(kind === "before" ? "touch-drop-before" : "touch-drop-after");
    }
  }

  function cancelTouchDrag(event?: React.PointerEvent<HTMLButtonElement>) {
    if (longPressTimer.current !== null) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    if (event && swipeRemove.current.pointerId === event.pointerId) {
      const item = event.currentTarget.closest<HTMLElement>(".action-item");
      item?.classList.remove("swipe-removing", "swipe-remove-armed", "swipe-remove-committing");
      item?.style.removeProperty("--swipe-remove-offset");
      swipeRemove.current = { action: swipeRemove.current.action, pointerId: null, startX: 0, startY: 0, armed: false };
    }
    if (event?.pointerType === "mouse" && smoothDrag.current?.touchId === event.pointerId) {
      const drag = smoothDrag.current;
      if (drag.active) clearSmoothActionDrag(drag);
      else smoothDrag.current = null;
      return;
    }
    if (!touchDrag.current.active) return;
    if (event && touchDrag.current.pointerId === event.pointerId) event.preventDefault();
    clearTouchDropPreview();
  }

  async function finishTouchDrag(event: React.PointerEvent<HTMLButtonElement>) {
    const pointerDrag = smoothDrag.current;
    if (event.pointerType === "mouse" && pointerDrag?.touchId === event.pointerId) {
      if (!pointerDrag.active) { smoothDrag.current = null; return; }
      event.preventDefault();
      try { event.currentTarget.releasePointerCapture?.(event.pointerId); } catch { /* Pointer capture may already be released. */ }
      finishSmoothActionDrag(pointerDrag);
      return;
    }
    if (smoothDrag.current?.active) { event.preventDefault(); return; }
    if (longPressTimer.current !== null) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    if (swipeRemove.current.pointerId === event.pointerId && onRemoveFromToday && !readOnly) {
      const { action, armed } = swipeRemove.current;
      const item = event.currentTarget.closest<HTMLElement>(".action-item");
      swipeRemove.current = { action, pointerId: null, startX: 0, startY: 0, armed: false };
      if (armed) {
        event.preventDefault();
        suppressClick.current = true;
        window.setTimeout(() => { suppressClick.current = false; }, 600);
        if ("vibrate" in navigator) navigator.vibrate?.(10);
        item?.classList.remove("swipe-removing", "swipe-remove-armed");
        item?.classList.add("swipe-remove-committing");
        item?.style.removeProperty("--swipe-remove-offset");
        await new Promise((resolve) => window.setTimeout(resolve, 180));
        await onRemoveFromToday(action);
        return;
      }
      item?.classList.remove("swipe-removing", "swipe-remove-armed");
      item?.style.removeProperty("--swipe-remove-offset");
    }
    if (!touchDrag.current.active || touchDrag.current.pointerId !== event.pointerId) return;
    event.preventDefault();
    suppressClick.current = true;
    window.setTimeout(() => { suppressClick.current = false; }, 600);
    const { action, destination } = touchDrag.current;
    touchDrag.current = { action, active: false, destination: null, pointerId: null };
    clearTouchDropPreview();
    if (destination) {
      if ("vibrate" in navigator) navigator.vibrate?.(10);
      await onMoveAction?.(action, destination);
    }
  }

  function rootAction(action: Action) {
    const root = action.parent_id ? allActions.find((item) => item.id === action.parent_id) || action : action;
    if (!ticketBlockReorder || root.parent_id) return root;
    return actionTicketBlockRoots(allActions, root)[0] || root;
  }

  function clearSmoothActionDrag(drag: NonNullable<typeof smoothDrag.current>) {
    const pointerCleanup = smoothPointerCleanup.current;
    smoothPointerCleanup.current = null;
    pointerCleanup?.();
    if (drag.scrollFrame !== null) window.cancelAnimationFrame(drag.scrollFrame);
    if (drag.previewFrame !== null) window.cancelAnimationFrame(drag.previewFrame);
    drag.orders.forEach((_order, list) => list.classList.remove("smooth-drop-empty"));
    drag.blocks.forEach((block) => block.items.forEach((item) => {
      item.style.transition = "";
      item.style.transform = "";
      item.style.opacity = "";
      item.style.willChange = "";
    }));
    drag.ghost?.remove();
    if (smoothDrag.current === drag) smoothDrag.current = null;
    window.setTimeout(() => { suppressClick.current = false; }, 500);
  }

  function finishSmoothActionDrag(drag: NonNullable<typeof smoothDrag.current>) {
    if (drag.previewFrame !== null) {
      window.cancelAnimationFrame(drag.previewFrame);
      drag.previewFrame = null;
      previewSmoothActionDestination(drag, drag.x, drag.y);
    }
    const destination = drag.destination;
    if (!destination) { clearSmoothActionDrag(drag); return; }
    drag.active = false;
    void onMoveAction?.(drag.source, destination);
    window.requestAnimationFrame(() => clearSmoothActionDrag(drag));
  }

  function layoutActionBlocks(drag: NonNullable<typeof smoothDrag.current>, list: HTMLElement, desired: string[]) {
    const original = drag.orders.get(list) || [];
    if (!original.length) return;
    const gaps = original.slice(0, -1).map((id, index) => {
      const current = drag.blocks.get(id)?.rect;
      const next = drag.blocks.get(original[index + 1])?.rect;
      return current && next ? Math.max(0, next.top - current.bottom) : 0;
    });
    const gap = gaps.length ? gaps.reduce((sum, value) => sum + value, 0) / gaps.length : 0;
    let nextTop = drag.blocks.get(original[0])?.rect.top || 0;
    const desiredTop = new Map<string, number>();
    for (const id of desired) {
      desiredTop.set(id, nextTop);
      nextTop += (drag.blocks.get(id)?.rect.height || 0) + gap;
    }
    for (const id of original) {
      if (id === drag.source.id) continue;
      const block = drag.blocks.get(id);
      const top = desiredTop.get(id);
      if (!block || top === undefined) continue;
      const offset = top - block.rect.top;
      block.items.forEach((item) => { item.style.transform = `translate3d(0, ${offset}px, 0)`; });
    }
  }

  function previewSmoothActionDestination(drag: NonNullable<typeof smoothDrag.current>, clientX: number, clientY: number) {
    if (drag.ghost) drag.ghost.style.transform = `translate3d(${clientX - drag.startX}px, ${clientY - drag.startY}px, 0) scale(1.018)`;
    drag.orders.forEach((_order, list) => list.classList.remove("smooth-drop-empty"));
    drag.blocks.forEach((block) => block.items.forEach((item) => { if (block.id !== drag.source.id) item.style.transform = "translate3d(0, 0, 0)"; }));
    const hoveredList = document.elementFromPoint(clientX, clientY)?.closest<HTMLElement>('.action-list[data-smooth-reorder="true"]');
    const hoveredOrder = hoveredList ? drag.orders.get(hoveredList) : undefined;
    if (hoveredList && hoveredOrder?.length === 0 && hoveredList.dataset.actionLane) {
      drag.destination = { kind: "lane", lane: hoveredList.dataset.actionLane as ActionLane };
      const sourceBlock = drag.blocks.get(drag.source.id);
      if (sourceBlock) layoutActionBlocks(drag, sourceBlock.list, (drag.orders.get(sourceBlock.list) || []).filter((id) => id !== drag.source.id));
      hoveredList.classList.add("smooth-drop-empty");
      return;
    }
    const candidates = [...drag.blocks.values()].filter((block) => block.id !== drag.source.id && (!hoveredList || block.list === hoveredList));
    const target = candidates
      .map((block) => ({ block, distance: Math.hypot(clientX - (block.rect.left + block.rect.width / 2), clientY - (block.rect.top + block.rect.height / 2)) }))
      .sort((a, b) => a.distance - b.distance)[0]?.block;
    if (!target) { drag.destination = null; return; }
    const placement: "before" | "after" = clientY < target.rect.top + target.rect.height / 2 ? "before" : "after";
    drag.destination = { kind: placement, target_id: target.action.id, target_revision: target.action.revision };
    const sourceBlock = drag.blocks.get(drag.source.id);
    if (!sourceBlock) return;
    const sourceList = sourceBlock.list;
    const targetList = target.list;
    const sourceOrder = drag.orders.get(sourceList) || [];
    const targetOrder = drag.orders.get(targetList) || [];
    if (sourceList === targetList) {
      const desired = sourceOrder.filter((id) => id !== drag.source.id);
      const targetIndex = desired.indexOf(target.id);
      desired.splice(Math.max(0, targetIndex + (placement === "after" ? 1 : 0)), 0, drag.source.id);
      layoutActionBlocks(drag, sourceList, desired);
      return;
    }
    layoutActionBlocks(drag, sourceList, sourceOrder.filter((id) => id !== drag.source.id));
    const desiredTarget = [...targetOrder];
    const targetIndex = desiredTarget.indexOf(target.id);
    desiredTarget.splice(Math.max(0, targetIndex + (placement === "after" ? 1 : 0)), 0, drag.source.id);
    layoutActionBlocks(drag, targetList, desiredTarget);
  }

  function queueSmoothActionPreview(drag: NonNullable<typeof smoothDrag.current>) {
    if (drag.previewFrame !== null) return;
    drag.previewFrame = window.requestAnimationFrame(() => {
      drag.previewFrame = null;
      if (smoothDrag.current === drag && drag.active) previewSmoothActionDestination(drag, drag.x, drag.y);
    });
  }

  function startSmoothActionAutoScroll(drag: NonNullable<typeof smoothDrag.current>) {
    const tick = () => {
      if (smoothDrag.current !== drag || !drag.active) return;
      const edge = Math.min(120, Math.max(76, window.innerHeight * 0.12));
      const bottomStart = window.innerHeight - edge;
      let velocity = 0;
      if (drag.y < edge) velocity = -Math.min(16, ((edge - drag.y) / edge) * 16);
      else if (drag.y > bottomStart) velocity = Math.min(16, ((drag.y - bottomStart) / edge) * 16);
      if (velocity !== 0) {
        const before = window.scrollY;
        window.scrollBy(0, velocity);
        const moved = window.scrollY - before;
        if (moved !== 0) {
          drag.blocks.forEach((block) => {
            block.rect = new DOMRect(block.rect.left, block.rect.top - moved, block.rect.width, block.rect.height);
          });
          queueSmoothActionPreview(drag);
        }
      }
      drag.scrollFrame = window.requestAnimationFrame(tick);
    };
    drag.scrollFrame = window.requestAnimationFrame(tick);
  }

  function activateSmoothActionDrag(drag: NonNullable<typeof smoothDrag.current>) {
    if (smoothDrag.current !== drag) return;
    drag.active = true;
    drag.startX = drag.x;
    drag.startY = drag.y;
    suppressClick.current = true;
    const page = drag.blocks.size ? null : document.querySelector("section.page");
    const lists = Array.from((page || document).querySelectorAll<HTMLElement>('.action-list[data-smooth-reorder="true"]'));
    const items = Array.from((page || document).querySelectorAll<HTMLElement>('.action-list[data-smooth-reorder="true"] .action-item[data-action-id]'));
    const blocks = new Map<string, { id: string; action: Action; items: HTMLElement[]; rect: DOMRect; list: HTMLElement }>();
    const orders = new Map<HTMLElement, string[]>();
    lists.forEach((list) => orders.set(list, []));
    for (const item of items) {
      const action = allActions.find((candidate) => candidate.id === item.dataset.actionId);
      const root = action ? rootAction(action) : undefined;
      const list = item.closest<HTMLElement>(".action-list");
      if (!root || !list || root.completed_at) continue;
      const existing = blocks.get(root.id);
      if (existing) existing.items.push(item);
      else blocks.set(root.id, { id: root.id, action: root, items: [item], rect: item.getBoundingClientRect(), list });
      const order = orders.get(list) || [];
      if (!order.includes(root.id)) order.push(root.id);
      orders.set(list, order);
    }
    blocks.forEach((block) => {
      const rects = block.items.map((item) => item.getBoundingClientRect());
      const left = Math.min(...rects.map((rect) => rect.left));
      const top = Math.min(...rects.map((rect) => rect.top));
      const right = Math.max(...rects.map((rect) => rect.right));
      const bottom = Math.max(...rects.map((rect) => rect.bottom));
      block.rect = new DOMRect(left, top, right - left, bottom - top);
      block.items.forEach((item) => {
        item.style.transition = "transform 240ms cubic-bezier(.2,.8,.2,1)";
        item.style.willChange = "transform";
      });
    });
    drag.blocks = blocks;
    drag.orders = orders;
    const sourceBlock = blocks.get(drag.source.id);
    if (!sourceBlock) return;
    const ghost = document.createElement("div");
    ghost.className = "action-drag-ghost";
    ghost.style.left = `${sourceBlock.rect.left}px`;
    ghost.style.top = `${sourceBlock.rect.top}px`;
    ghost.style.width = `${sourceBlock.rect.width}px`;
    sourceBlock.items.forEach((item) => {
      const clone = item.cloneNode(true) as HTMLElement;
      clone.style.transform = "";
      clone.style.opacity = "";
      clone.querySelectorAll<HTMLElement>("button,input").forEach((control) => { control.setAttribute("disabled", "true"); });
      ghost.appendChild(clone);
      item.style.opacity = "0";
    });
    document.body.appendChild(ghost);
    drag.ghost = ghost;
    startSmoothActionAutoScroll(drag);
    if ("vibrate" in navigator) navigator.vibrate?.(12);
  }

  function beginSmoothActionTouch(action: Action, event: React.TouchEvent<HTMLButtonElement>) {
    if (!online || readOnly || !onMoveAction || smoothDrag.current) return;
    const touch = event.changedTouches[0];
    if (!touch) return;
    const source = rootAction(action);
    const drag = { source, touchId: touch.identifier, active: false, startX: touch.clientX, startY: touch.clientY, x: touch.clientX, y: touch.clientY, timer: 0, destination: null, ghost: null, scrollFrame: null, previewFrame: null, blocks: new Map(), orders: new Map() };
    const cleanup = () => {
      document.removeEventListener("touchmove", onMove, true);
      document.removeEventListener("touchend", onEnd, true);
      document.removeEventListener("touchcancel", onEnd, true);
      if (smoothTouchCleanup.current === cleanup) smoothTouchCleanup.current = null;
    };
    const onMove = (nativeEvent: TouchEvent) => {
      const currentTouch = Array.from(nativeEvent.touches).find((item) => item.identifier === drag.touchId);
      if (!currentTouch || smoothDrag.current !== drag) return;
      drag.x = currentTouch.clientX;
      drag.y = currentTouch.clientY;
      if (!drag.active) {
        if (Math.hypot(drag.x - drag.startX, drag.y - drag.startY) > 8) {
          window.clearTimeout(drag.timer);
          smoothDrag.current = null;
          cleanup();
        }
        return;
      }
      nativeEvent.preventDefault();
      nativeEvent.stopPropagation();
      queueSmoothActionPreview(drag);
    };
    const onEnd = (nativeEvent: TouchEvent) => {
      if (!Array.from(nativeEvent.changedTouches).some((item) => item.identifier === drag.touchId)) return;
      window.clearTimeout(drag.timer);
      cleanup();
      if (!drag.active) { smoothDrag.current = null; return; }
      nativeEvent.preventDefault();
      finishSmoothActionDrag(drag);
    };
    drag.timer = window.setTimeout(() => activateSmoothActionDrag(drag), 320);
    smoothDrag.current = drag;
    smoothTouchCleanup.current?.();
    smoothTouchCleanup.current = cleanup;
    document.addEventListener("touchmove", onMove, { passive: false, capture: true });
    document.addEventListener("touchend", onEnd, { passive: false, capture: true });
    document.addEventListener("touchcancel", onEnd, { passive: false, capture: true });
  }

  function activateFromClick(action: Action, clickCount: number) {
    if (suppressClick.current) { suppressClick.current = false; return; }
    if (clickCount >= 2) {
      if (singleClickTimer.current !== null) window.clearTimeout(singleClickTimer.current);
      singleClickTimer.current = null;
      onStartMove?.(action);
      return;
    }
    if (singleClickTimer.current !== null) window.clearTimeout(singleClickTimer.current);
    singleClickTimer.current = window.setTimeout(() => {
      singleClickTimer.current = null;
      onSelect?.(action);
    }, 260);
  }

  async function toggleCompletion(action: Action) {
    if (action.id in pendingCompletion) return;
    if (!action.completed_at && allActions.some((item) => item.parent_id === action.id && !item.completed_at)) {
      window.alert("未完了のサブアクションがあります。先にサブアクションを完了してください。");
      return;
    }
    const completed = !action.completed_at;
    setPendingCompletion((current) => ({ ...current, [action.id]: completed }));
    if (completed) {
      setLeavingCompletionIds((current) => new Set(current).add(action.id));
      window.setTimeout(() => {
        setLeavingCompletionIds((current) => { const next = new Set(current); next.delete(action.id); return next; });
        setHiddenCompletionIds((current) => new Set(current).add(action.id));
      }, 160);
    }
    try {
      await repository!.updateAction(action.id, action.revision, { completed });
      await refresh();
    } catch (cause) {
      await refresh();
      window.alert(`完了状態を更新できませんでした。\n${cause instanceof Error ? cause.message : "通信状態を確認して、もう一度お試しください。"}`);
    } finally {
      setLeavingCompletionIds((current) => { const next = new Set(current); next.delete(action.id); return next; });
      setHiddenCompletionIds((current) => { const next = new Set(current); next.delete(action.id); return next; });
      setPendingCompletion((current) => {
        const next = { ...current };
        delete next[action.id];
        return next;
      });
    }
  }

  const renderedActions = actions.filter((action) => !hiddenCompletionIds.has(action.id));
  if (!renderedActions.length) return <div className={`action-list action-list-empty ${compact ? "compact" : ""}`} data-smooth-reorder={Boolean(onMoveAction && !readOnly)} data-action-lane={lane}><Empty text="ここに表示するアクションはありません。" /></div>;
  return <div className={`action-list ${compact ? "compact" : ""}`} data-smooth-reorder={Boolean(onMoveAction && !readOnly)} data-action-lane={lane}>{renderedActions.map((action) => {
    const selectable = Boolean(onSelect && !readOnly);
    const selected = selectedId === action.id;
    const completionPending = action.id in pendingCompletion;
    const displayedCompleted = completionPending ? pendingCompletion[action.id] : Boolean(action.completed_at);
    const linkedTicket = action.ticket_key ? byKey.get(action.ticket_key) : action.ticket_id ? byId.get(action.ticket_id) : undefined;
    const ticketLabel = linkedTicket?.title || "";
    const contextLabel = action.context && action.context !== ticketLabel ? action.context : "";
    const parentVisible = Boolean(action.parent_id && visibleIds.has(action.parent_id));
    const parentAction = action.parent_id ? allActions.find((item) => item.id === action.parent_id) : undefined;
    const childCount = allActions.filter((item) => item.parent_id === action.id && !item.completed_at).length;
    const relationBadge = flatReorder
      ? action.parent_id
        ? { kind: "child" as const, label: "親", detail: parentAction?.text || "親アクション" }
        : childCount > 0
          ? { kind: "parent" as const, label: `子 ${childCount}`, detail: "" }
          : null
      : null;
    const details = [
      parentVisible ? "サブアクション" : !flatReorder && parentAction ? `親: ${parentAction.text}` : !flatReorder && action.parent_id ? "サブアクション" : "",
      ticketLabel,
      action.project_name,
      contextLabel,
      action.reminder_on || "リマインド未設定",
    ].filter(Boolean).join(" · ");
    const canReceiveChild = Boolean(moving && onMoveAction && !movingHasChildren && !moving.parent_id && !action.parent_id && !movingActionIds.has(action.id));
    const childTargetBlocked = Boolean(
      moving &&
      onMoveAction &&
      movingHasChildren &&
      !moving.parent_id &&
      !action.parent_id &&
      !movingActionIds.has(action.id)
    );
    const canReorderBefore = Boolean(moving && onMoveAction && reorderTargets.some((target) => target.id === action.id));
    return <div data-action-id={action.id} className={`action-item ${selected ? "selected" : ""} ${movingActionIds.has(action.id) ? "moving" : ""} ${highlightedId === action.id ? "just-created" : ""} ${leavingCompletionIds.has(action.id) ? "completion-leaving" : ""} ${onAddToToday && todayActionIds?.has(action.id) ? "is-today-added" : ""}`} key={action.id}>
      {canReorderBefore && <button type="button" className="order-drop-zone" onClick={() => onMoveAction!(moving!, { kind: "before", target_id: action.id, target_revision: action.revision })}><span>ここへ移動</span></button>}
      {onRemoveFromToday && !readOnly && <button type="button" className="swipe-remove-band" tabIndex={-1} aria-hidden="true" disabled={!online || Boolean(moving) || completionPending || pendingLaneIds?.has(action.id) || pendingTodayIds?.has(action.id)} onClick={() => void onRemoveFromToday(action)}>外す</button>}
      <div className={`action-row lane-${action.lane} ${parentVisible ? "child" : ""} ${canReceiveChild ? "can-receive-child" : ""} ${childTargetBlocked ? "child-target-blocked" : ""}`}>
        <input aria-label={`${action.text}を完了`} type="checkbox" checked={displayedCompleted} disabled={!online || readOnly || Boolean(moving) || completionPending || pendingLaneIds?.has(action.id) || pendingTodayIds?.has(action.id)} onChange={() => toggleCompletion(action)} />
        {selectable ? <button type="button" className="action-main action-select" aria-expanded={selected} aria-disabled={Boolean(moving && !movingActionIds.has(action.id))} onClick={(event) => { if (moving && !movingActionIds.has(action.id)) { event.preventDefault(); return; } activateFromClick(action, event.detail); }} onDoubleClick={(event) => event.preventDefault()} onTouchStart={(event) => beginSmoothActionTouch(action, event)} onPointerDown={(event) => beginTouchDrag(action, event)} onPointerMove={updateTouchDrag} onPointerUp={finishTouchDrag} onPointerCancel={cancelTouchDrag}><strong className="action-title-line"><span>{action.text}</span>{relationBadge?.kind === "parent" && <span className="relation-badge relation-badge-parent">{relationBadge.label}</span>}{relationBadge?.kind === "child" && <span className="relation-parent-inline"><span className="relation-badge relation-badge-child">{relationBadge.label}</span><span className="relation-parent-text">{relationBadge.detail}</span></span>}</strong><small>{details}</small></button> : <span className="action-main"><strong className="action-title-line"><span>{action.text}</span>{relationBadge?.kind === "parent" && <span className="relation-badge relation-badge-parent">{relationBadge.label}</span>}{relationBadge?.kind === "child" && <span className="relation-parent-inline"><span className="relation-badge relation-badge-child">{relationBadge.label}</span><span className="relation-parent-text">{relationBadge.detail}</span></span>}</strong><small>{details}</small></span>}
        {showLaneBadge && (onCycleLane && !readOnly
          ? <LaneCycleButton action={action} disabled={!online || Boolean(moving) || completionPending || Boolean(pendingTodayIds?.has(action.id))} onCycle={onCycleLane} />
          : <span className={`lane-chip lane-chip-${action.lane}`}>{laneLabels[action.lane]}</span>)}
        {!readOnly && (onRemoveFromToday
          ? <button type="button" className="today-remove-action" aria-label={`${action.text}を今日から外す`} title="今日から外す" disabled={!online || Boolean(moving) || completionPending || pendingLaneIds?.has(action.id) || pendingTodayIds?.has(action.id)} onClick={() => void onRemoveFromToday(action)}>外す</button>
          : onAddToToday
            ? <button type="button" className={`action-today-add ${todayActionIds?.has(action.id) ? "is-added" : ""}`} aria-pressed={todayActionIds?.has(action.id) ? "true" : "false"} aria-label={todayActionIds?.has(action.id) ? `${action.text}を今日から外す` : `${action.text}を今日に追加`} title={todayActionIds?.has(action.id) ? "クリックで今日から外す" : "クリックで今日に追加"} disabled={!online || Boolean(moving) || completionPending || pendingLaneIds?.has(action.id) || pendingTodayIds?.has(action.id)} onClick={(event) => { event.preventDefault(); event.stopPropagation(); void onAddToToday(action); }}>{todayActionIds?.has(action.id) ? "追加済" : "追加"}</button>
            : <button type="button" className="danger-icon" aria-label={`${action.text}を削除`} disabled={!online || Boolean(moving)} onClick={async () => { if (!window.confirm(`「${action.text}」を削除しますか？ 30日間はゴミ箱に残ります。`)) return; await repository!.trash("action", action); await refresh(); }}>×</button>)}
        {canReceiveChild && <button type="button" className="child-drop-zone" aria-label={`${moving!.text}を${action.text}の子アクションにする`} onClick={() => onMoveAction!(moving!, { kind: "child", target_id: action.id, target_revision: action.revision })}><span>ここに重ねて子アクションにする</span></button>}
        {childTargetBlocked && <button type="button" className="child-drop-zone child-drop-zone-blocked" aria-disabled="true" onClick={(event) => { event.preventDefault(); event.stopPropagation(); }}><span>このアイテムは子アクションにできません</span></button>}
      </div>
      {moving && lastReorderTarget?.id === action.id && (flatReorder || moving.parent_id) && <button type="button" className="order-drop-zone after" onClick={() => onMoveAction!(moving, { kind: "after", target_id: action.id, target_revision: action.revision })}><span>ここへ移動</span></button>}
    </div>;
  })}{moving && (!moving.parent_id || flatReorder) && lastReorderTarget && actions.some((action) => action.id === lastReorderTarget.id) && <button type="button" className="order-drop-zone after-list" onClick={() => onMoveAction!(moving, { kind: "after", target_id: lastReorderTarget.id, target_revision: lastReorderTarget.revision })}><span>ここへ移動</span></button>}</div>;
}

function ActionDetails({ action, allActions, ticketCatalog, online, refresh, onSaved, onAddSubAction, onDirtyChange, todayActionIds, pendingTodayIds, onToggleRelatedToday, keepOnTodayDate }: { action: Action; allActions: Action[]; ticketCatalog: TicketCatalog[]; online: boolean; refresh: () => Promise<void>; onSaved?: () => void; onAddSubAction?: (action: Action) => void; onDirtyChange?: (dirty: boolean) => void; todayActionIds?: Set<string>; pendingTodayIds?: Set<string>; onToggleRelatedToday?: (action: Action) => Promise<void>; keepOnTodayDate?: string }) {
  const storedDraft = actionEditDrafts.get(action.id);
  const [text, setText] = useState(storedDraft?.text ?? action.text);
  const [note, setNote] = useState(storedDraft?.note ?? action.note);
  const [lane, setLane] = useState<ActionLane>(storedDraft?.lane ?? action.lane);
  const [ticketKey, setTicketKey] = useState(storedDraft?.ticketKey ?? action.ticket_key ?? "");
  const [projectName, setProjectName] = useState(storedDraft?.projectName ?? action.project_name);
  const [context, setContext] = useState(storedDraft?.context ?? action.context);
  const [referenceUrl, setReferenceUrl] = useState(storedDraft?.referenceUrl ?? action.reference_url);
  const [reminderOn, setReminderOn] = useState(storedDraft?.reminderOn ?? action.reminder_on ?? "");
  const [reminderMode, setReminderMode] = useState<ReminderMode>(storedDraft?.reminderMode ?? action.reminder_mode ?? "persistent");
  const [saving, setSaving] = useState(false);
  const [cloudChanged, setCloudChanged] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [pendingRelatedCompletion, setPendingRelatedCompletion] = useState<Record<string, boolean>>({});
  const dirtyRef = useRef(Boolean(storedDraft));
  const actionIdRef = useRef(action.id);
  const revisionRef = useRef(storedDraft?.baseRevision ?? action.revision);
  const reminderPickerRef = useRef<HTMLInputElement | null>(null);
  const dirty = text !== action.text || note !== action.note || lane !== action.lane || ticketKey !== (action.ticket_key || "") || projectName !== action.project_name || context !== action.context || referenceUrl !== action.reference_url || reminderOn !== (action.reminder_on || "") || reminderMode !== (action.reminder_mode || "persistent");
  const draftBaseRevision = actionEditDrafts.get(action.id)?.baseRevision;
  const hasCloudUpdate = cloudChanged || (draftBaseRevision !== undefined && draftBaseRevision !== action.revision);

  function loadLatestIntoForm() {
    setText(action.text);
    setNote(action.note);
    setLane(action.lane);
    setTicketKey(action.ticket_key || "");
    setProjectName(action.project_name);
    setContext(action.context);
    setReferenceUrl(action.reference_url);
    setReminderOn(action.reminder_on || "");
    setReminderMode(action.reminder_mode || "persistent");
    setCloudChanged(false);
    setSaveError("");
    actionEditDrafts.delete(action.id);
    dirtyRef.current = false;
  }

  useEffect(() => {
    const actionChanged = action.id !== actionIdRef.current;
    const revisionChanged = action.revision !== revisionRef.current;
    if (actionChanged) {
      const draft = actionEditDrafts.get(action.id);
      if (draft) {
        setText(draft.text);
        setNote(draft.note);
        setLane(draft.lane);
        setTicketKey(draft.ticketKey);
        setProjectName(draft.projectName);
        setContext(draft.context);
        setReferenceUrl(draft.referenceUrl);
        setReminderOn(draft.reminderOn);
        setReminderMode(draft.reminderMode);
        dirtyRef.current = true;
        setCloudChanged(action.revision !== draft.baseRevision);
      } else {
        loadLatestIntoForm();
      }
    } else if (revisionChanged) {
      if (dirtyRef.current || actionEditDrafts.has(action.id)) setCloudChanged(true);
      else loadLatestIntoForm();
    }
    actionIdRef.current = action.id;
    revisionRef.current = action.revision;
  }, [action.id, action.revision, action.text, action.note, action.lane, action.ticket_key, action.project_name, action.context, action.reference_url, action.reminder_on, action.reminder_mode]);

  useEffect(() => {
    dirtyRef.current = dirty;
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    if (!dirtyRef.current && !dirty) return;
    actionEditDrafts.set(action.id, {
      text,
      note,
      lane,
      ticketKey,
      projectName,
      context,
      referenceUrl,
      reminderOn,
      reminderMode,
      baseRevision: actionEditDrafts.get(action.id)?.baseRevision ?? revisionRef.current,
    });
  }, [action.id, text, note, lane, ticketKey, projectName, context, referenceUrl, reminderOn, reminderMode, dirty]);


  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  function markDirty() {
    dirtyRef.current = true;
  }

  function changeTicket(nextKey: string) {
    markDirty();
    setTicketKey(nextKey);
    const selected = ticketCatalog.find((ticket) => ticket.ticket_key === nextKey);
    if (selected) {
      setProjectName(selected.project);
      setContext(selected.title);
    }
  }

  async function changeParent(nextParentId: string) {
    if (!online || dirty || saving || !action.parent_id || nextParentId === action.parent_id) return;
    setSaving(true);
    setSaveError("");
    try {
      if (!nextParentId) {
        await repository!.updateAction(action.id, action.revision, { parent_id: null });
      } else {
        const nextParent = allActions.find((item) => item.id === nextParentId && !item.parent_id && !item.completed_at);
        if (!nextParent) throw new Error("選択した親アクションが見つかりません。");
        await repository!.moveAction(action, {
          kind: "child",
          target_id: nextParent.id,
          target_revision: nextParent.revision,
        });
      }
      actionEditDrafts.delete(action.id);
      dirtyRef.current = false;
      await refresh();
    } catch (cause) {
      setSaveError(cause instanceof Error ? cause.message : "親アクションを変更できませんでした。");
    } finally {
      setSaving(false);
    }
  }

  async function save() {
    if (!online || !dirty || saving) return;
    if (hasCloudUpdate) {
      setSaveError("クラウド側に更新があります。先に最新状態を読み込んでください。");
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      const expectedRevision = actionEditDrafts.get(action.id)?.baseRevision ?? action.revision;
      let revision = expectedRevision;
      const nextTicketKey = ticketKey || null;
      const ticketChanged = nextTicketKey !== (action.ticket_key || null);
      const detailsChanged = text !== action.text || note !== action.note || projectName !== action.project_name || context !== action.context || referenceUrl !== action.reference_url || reminderOn !== (action.reminder_on || "") || reminderMode !== (action.reminder_mode || "persistent");
      const keepTodayFields = keepOnTodayDate && reminderOn !== (action.reminder_on || "")
        ? { today_override_on: keepOnTodayDate, today_override_visible: true }
        : {};
      if (ticketChanged) {
        if (action.parent_id) throw new Error("サブアクション単体では関連チケットを変更できません");
        if (text !== action.text || note !== action.note || referenceUrl !== action.reference_url || reminderOn !== (action.reminder_on || "") || reminderMode !== (action.reminder_mode || "persistent")) {
          const updated = await repository!.updateAction(action.id, revision, { text: text.trim(), note, reference_url: referenceUrl.trim(), reminder_on: reminderOn || null, reminder_mode: reminderMode, ...keepTodayFields });
          revision = updated.revision;
        }
        const children = allActions.filter((item) => item.parent_id === action.id);
        revision = await repository!.changeActionTicketContext({ id: action.id, revision }, children, {
          ticket_key: nextTicketKey,
          project_name: projectName.trim(),
          context: context.trim(),
        });
      } else if (detailsChanged) {
        const updated = await repository!.updateAction(action.id, revision, { text: text.trim(), note, project_name: projectName.trim(), context: context.trim(), reference_url: referenceUrl.trim(), reminder_on: reminderOn || null, reminder_mode: reminderMode, ...keepTodayFields });
        revision = updated.revision;
      }
      if (lane !== action.lane) await repository!.changeActionLane({ id: action.id, revision }, lane);
      actionEditDrafts.delete(action.id);
      dirtyRef.current = false;
      setCloudChanged(false);
      await refresh();
      onSaved?.();
    } catch (cause) {
      await refresh();
      setCloudChanged(true);
      setSaveError(cause instanceof Error ? cause.message : "保存できませんでした。最新状態を確認してください。");
    } finally {
      setSaving(false);
    }
  }

  function discardAndLoadLatest() {
    if (dirty && !window.confirm("未保存の入力を破棄して最新状態を読み込みますか？")) return;
    loadLatestIntoForm();
  }

  const parentCandidates = allActions
    .filter((item) => !item.parent_id && !item.completed_at && item.id !== action.id)
    .sort((a, b) => a.sort_order - b.sort_order || a.text.localeCompare(b.text, "ja"));
  const parentAction = action.parent_id ? allActions.find((item) => item.id === action.parent_id) : undefined;
  const childActions = allActions
    .filter((item) => item.parent_id === action.id)
    .sort((a, b) => {
      if (a.completed_at && b.completed_at) return new Date(b.completed_at).getTime() - new Date(a.completed_at).getTime();
      if (a.completed_at) return 1;
      if (b.completed_at) return -1;
      return a.sort_order - b.sort_order;
    });
  const relationActions = [
    ...(parentAction ? [{ relation: "親" as const, action: parentAction }] : []),
    ...childActions.map((item) => ({ relation: "子" as const, action: item })),
  ];
  async function toggleRelatedCompletion(related: Action) {
    if (!online || related.id in pendingRelatedCompletion || pendingTodayIds?.has(related.id)) return;
    if (!related.completed_at && allActions.some((item) => item.parent_id === related.id && !item.completed_at)) {
      window.alert("未完了のサブアクションがあります。先にサブアクションを完了してください。");
      return;
    }
    const completed = !related.completed_at;
    setPendingRelatedCompletion((current) => ({ ...current, [related.id]: completed }));
    try {
      await repository!.updateAction(related.id, related.revision, { completed });
      await refresh();
    } catch (cause) {
      await refresh();
      window.alert(cause instanceof Error ? cause.message : "完了状態を更新できませんでした");
    } finally {
      setPendingRelatedCompletion((current) => { const next = { ...current }; delete next[related.id]; return next; });
    }
  }
  return <form className="action-details" onSubmit={(event) => { event.preventDefault(); void save(); }}>
    {hasCloudUpdate && <div className="action-cloud-update" role="status"><span>クラウド側に更新があります。未保存の入力は保持しています。</span><button type="button" className="text-button" onClick={discardAndLoadLatest}>最新状態を読み込む</button></div>}
    {saveError && <div className="error" role="alert">{saveError}</div>}
    <div className="action-details-heading"><div><span>アクション情報</span><small>{note.length}/{ACTION_NOTE_MAX_LENGTH}</small></div>{!action.parent_id && onAddSubAction && <button type="button" className="sub-action-button" disabled={!online} onClick={() => onAddSubAction(action)}>＋ サブアクションを追加</button>}</div>
    {relationActions.length > 0 && <section className="action-relations" aria-label="親子アクション">
      <div className="action-relations-heading">親子アクション</div>
      <div className="action-relations-list">{relationActions.map(({ relation, action: related }) => {
        const alreadyToday = todayActionIds?.has(related.id) ?? false;
        const completionPending = related.id in pendingRelatedCompletion;
        const displayedCompleted = completionPending ? pendingRelatedCompletion[related.id] : Boolean(related.completed_at);
        const todayPending = pendingTodayIds?.has(related.id) ?? false;
        const relationPending = completionPending || todayPending;
        return <div className={`action-relation-row ${displayedCompleted ? "completed" : ""}`} key={related.id}>
          <input className="action-relation-check" type="checkbox" checked={displayedCompleted} disabled={!online || relationPending} aria-label={`${related.text}を${displayedCompleted ? "未完了に戻す" : "完了にする"}`} onChange={() => void toggleRelatedCompletion(related)} />
          <span className={`relation-badge ${relation === "親" ? "relation-badge-parent" : "relation-badge-child"}`}>{relation}</span>
          <div className="action-relation-copy"><strong>{related.text}</strong></div>
          <div className="action-relation-action">
            {onToggleRelatedToday && !displayedCompleted && <button type="button" className={`relation-today-button ${alreadyToday ? "is-added" : ""}`} aria-pressed={alreadyToday} disabled={!online || relationPending} onClick={() => void onToggleRelatedToday(related)}>{alreadyToday ? "追加済" : "追加"}</button>}
          </div>
        </div>;
      })}</div>
    </section>}
    <label className="action-title-edit">アクション名<input value={text} onChange={(event) => { markDirty(); setText(event.target.value); }} maxLength={500} /></label>
    <div className="action-context-grid">
      <label>優先度<select value={lane} disabled={saving || !online} onChange={(event) => { markDirty(); setLane(event.target.value as ActionLane); }}>{Object.entries(laneLabels).map(([id, label]) => <option key={id} value={id}>{label}</option>)}</select>{action.parent_id && <small className="field-help">表示位置は親アクションの優先度に従います</small>}</label>
      {action.parent_id && <label className="wide-field">親アクション<select value={action.parent_id} disabled={dirty || saving || !online} onChange={(event) => changeParent(event.target.value)}><option value="">単体アクションに戻す</option>{parentCandidates.map((parent) => <option key={parent.id} value={parent.id}>{parent.text}</option>)}</select>{dirty && <small className="field-help">未保存の編集を保存してから変更できます</small>}</label>}
      <div className="wide-field action-ticket-field"><span className="field-label">関連チケット</span><TicketPicker tickets={ticketCatalog} value={ticketKey} onChange={changeTicket} disabled={Boolean(action.parent_id)} />{action.parent_id && <small className="field-help">親アクションの関連チケットを使用します</small>}</div>
      <label className="compact-date-field">リマインド<div className="action-date-with-reset"><div className="action-date-control"><span className="action-date-display">{reminderOn ? reminderOn.replaceAll("-", "/") : "未設定"}</span><input ref={reminderPickerRef} className="action-date-native" aria-label="リマインド日を選択" type="date" value={reminderOn} onChange={(event) => { markDirty(); setReminderOn(event.target.value); }}/></div><button type="button" className="date-reset-button" disabled={!reminderOn} onClick={() => { markDirty(); setReminderOn(""); setReminderMode("persistent"); }}>リセット</button></div></label>
      <label>リマインド方法<select value={reminderMode} disabled={!reminderOn} onChange={(event) => { markDirty(); setReminderMode(event.target.value as ReminderMode); }}><option value="persistent">完了まで毎日</option><option value="once">その日だけ</option></select></label>
    </div>
    <div className={`action-note-field${note.trim() ? "" : " is-empty"}`}><div className="action-note-heading"><span>メモ（Markdown）</span><small>{note.length}/{ACTION_NOTE_MAX_LENGTH}</small></div><small className="markdown-notation-help">{MARKDOWN_NOTATION_HELP}</small><Suspense fallback={<div className="memo-editor-loading action-note-loading">エディタを準備中…</div>}><MemoMarkdownEditor value={note} onChange={(value) => { markDirty(); setNote(value); }} maxLength={ACTION_NOTE_MAX_LENGTH} ariaLabel="アクションのメモ"/></Suspense></div>
    <footer className="action-details-footer"><button type="button" className="danger action-delete-button" disabled={!online || saving} onClick={async () => { if (!window.confirm(`「${action.text}」を削除しますか？ 30日間はゴミ箱に残ります。`)) return; await repository!.trash("action", action); actionEditDrafts.delete(action.id); onDirtyChange?.(false); await refresh(); }}>削除</button><div className="action-details-save"><span>{saving ? "保存中…" : dirty ? "未保存" : action.note ? `v${action.revision}` : "保存済み"}</span><button type="submit" className="primary" disabled={!online || !dirty || !text.trim() || saving}>保存</button></div></footer>
  </form>;
}

function MoveStatus({ action, cancel }: { action?: Action; cancel: () => void }) {
  if (!action) return null;
  return <div className="move-status" role="status"><span><strong>移動中</strong>　{action.text}</span><span>並び替え・優先度変更・行の上でサブアクション化。外側をダブルクリック / ダブルタップで終了</span><button type="button" onClick={cancel}>キャンセル</button></div>;
}

function ActionsPage({ data, online, refresh, embedded = false, embeddedTicketKey = "" }: PageProps & { embedded?: boolean; embeddedTicketKey?: string }) {
  const [creating, setCreating] = useState(false);
  const [creatingForTicket, setCreatingForTicket] = useState<TicketCatalog | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [subActionParent, setSubActionParent] = useState<Action | null>(null);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [movingId, setMovingId] = useState<string | null>(null);
  const [detailDirty, setDetailDirty] = useState(false);
  const [todayOverrides, setTodayOverrides] = useState<Record<string, boolean>>({});
  const [pendingTodayIds, setPendingTodayIds] = useState<Set<string>>(new Set());
  const [movePreview, setMovePreview] = useState<{ sourceIds: string[]; targetId: string | null; placement: "before" | "after"; targetLane: ActionLane } | null>(null);
  const pendingTodayActions = useRef<Set<string>>(new Set());
  const open = data.actions.filter((x) => !x.completed_at);
  const displayedOpen = useMemo(() => {
    if (!movePreview) return open;
    const sourceIds = new Set(movePreview.sourceIds);
    const laneOrders = new Map<ActionLane, string[]>((Object.keys(laneLabels) as ActionLane[]).map((lane) => [lane, orderActionRootsInTicketBlocks(open.filter((action) => !action.parent_id && action.lane === lane)).map((action) => action.id)]));
    laneOrders.forEach((ids, lane) => laneOrders.set(lane, ids.filter((id) => !sourceIds.has(id))));
    const targetOrder = laneOrders.get(movePreview.targetLane) || [];
    const rawTarget = movePreview.targetId ? open.find((action) => action.id === movePreview.targetId) : undefined;
    const targetBlockIds = rawTarget ? actionTicketBlockRoots(open, rawTarget).map((action) => action.id).filter((id) => targetOrder.includes(id)) : [];
    const targetBoundaryId = movePreview.placement === "after" ? targetBlockIds.at(-1) : targetBlockIds[0];
    const targetIndex = targetBoundaryId ? targetOrder.indexOf(targetBoundaryId) : -1;
    if (movePreview.targetId && targetIndex < 0) return open;
    targetOrder.splice(movePreview.targetId ? targetIndex + (movePreview.placement === "after" ? 1 : 0) : targetOrder.length, 0, ...movePreview.sourceIds);
    const placement = new Map<string, { lane: ActionLane; sortOrder: number }>();
    laneOrders.forEach((ids, lane) => ids.forEach((id, index) => placement.set(id, { lane, sortOrder: index })));
    return open.map((action) => {
      if (action.parent_id) return action;
      const next = placement.get(action.id);
      return next ? { ...action, lane: next.lane, sort_order: next.sortOrder } : action;
    });
  }, [open, movePreview]);
  const selectedAction = selectedId ? data.actions.find((action) => action.id === selectedId) || null : null;
  const todayDate = todayInTokyo();
  const actualTodayIds = new Set(actionsForDaily(data.actions, todayDate, todayDate).map((action) => action.id));
  const displayedTodayIds = new Set(actualTodayIds);
  Object.entries(todayOverrides).forEach(([id, included]) => { if (included) displayedTodayIds.add(id); else displayedTodayIds.delete(id); });
  useEffect(() => {
    setTodayOverrides((current) => {
      const next = { ...current };
      let changed = false;
      for (const [id, included] of Object.entries(current)) {
        if (actualTodayIds.has(id) !== included) continue;
        delete next[id];
        changed = true;
      }
      return changed ? next : current;
    });
  }, [data.actions, todayDate]);
  useMobileBodyScrollLock(Boolean(creating || subActionParent || (selectedAction && !movingId)));
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("newAction") !== "1") return;
    const ticketKey = params.get("ticket");
    if (!ticketKey) return;
    const ticket = data.ticketCatalog.find((item) => item.ticket_key === ticketKey);
    if (!ticket) return;
    setCreatingForTicket(ticket);
    setCreating(true);
    const url = new URL(window.location.href);
    url.searchParams.delete("newAction");
    url.searchParams.delete("ticket");
    window.history.replaceState({}, "", url);
  }, [data.ticketCatalog]);
  useEffect(() => { if (selectedId && !open.some((action) => action.id === selectedId)) setSelectedId(null); }, [data.actions, selectedId]);
  useEffect(() => {
    if (!movingId) return;
    const cancelMove = (event: KeyboardEvent) => { if (event.key === "Escape") setMovingId(null); };
    window.addEventListener("keydown", cancelMove);
    return () => window.removeEventListener("keydown", cancelMove);
  }, [movingId]);
  useCancelMoveOnOutside(movingId, () => setMovingId(null));
  const embeddedTicket = embeddedTicketKey ? data.ticketCatalog.find((item) => item.ticket_key === embeddedTicketKey) : undefined;
  const embeddedActions = actionsForEmbeddedTicket(data.actions, embeddedTicketKey);
  if (embedded) {
    return <section className="embedded-actions">
      <header className="embedded-actions-header">
        <div><p className="eyebrow">DAILY DESK</p><h2>{embeddedTicket?.title || "関連アクション"}</h2></div>
        <span className="count-pill">{embeddedActions.length} 件</span>
      </header>
      {!embeddedTicketKey
        ? <Empty text="チケットが指定されていません。" />
        : !embeddedTicket
          ? <Empty text="Daily Deskに連携されていないチケットです。" />
          : <ActionList actions={orderActionsWithChildren(embeddedActions)} allActions={data.actions} tickets={data.tickets} ticketCatalog={data.ticketCatalog} online={online} refresh={refresh} compact readOnly />}
      {embeddedTicket && <footer className="embedded-actions-footer">
        <a className="secondary" href={embeddedActionUrl(embeddedTicket.ticket_key)} target="_blank" rel="noreferrer">Daily Deskで開く</a>
        <a className="primary" href={embeddedActionUrl(embeddedTicket.ticket_key, true)} target="_blank" rel="noreferrer">＋ アクション追加</a>
      </footer>}
    </section>;
  }
  function highlight(id: string) { setHighlightedId(id); window.setTimeout(() => setHighlightedId((current) => current === id ? null : current), 1800); }
  function closeActionDetail() { if (detailDirty && !window.confirm("未保存の入力を破棄して閉じますか？")) return; setSelectedId(null); setDetailDirty(false); }
  const actionDetailSwipe = useSwipeRightToClose(closeActionDetail);
  function selectAction(action: Action) { if (detailDirty && !window.confirm("未保存のアクションメモを破棄しますか？")) return; setSelectedId((current) => current === action.id ? null : action.id); setDetailDirty(false); }
  function beginSubAction(action: Action) {
    if (action.parent_id) return;
    if (detailDirty && !window.confirm("未保存のアクションメモを破棄してサブアクションを追加しますか？")) return;
    setSelectedId(null);
    setDetailDirty(false);
    setSubActionParent(action);
  }
  function startMove(action: Action) { if (detailDirty && !window.confirm("未保存のアクションメモを破棄して移動しますか？")) return; const root = action.parent_id ? data.actions.find((item) => item.id === action.parent_id) || action : action; const blockRoot = actionTicketBlockRoots(open, root)[0] || root; setSelectedId(null); setDetailDirty(false); setMovingId(blockRoot.id); }
  async function addActionToToday(action: Action) {
    if (!online || action.completed_at || pendingTodayActions.current.has(action.id)) return;
    const alreadyToday = displayedTodayIds.has(action.id);
    const nextIncluded = !alreadyToday;
    pendingTodayActions.current.add(action.id);
    setPendingTodayIds((current) => new Set(current).add(action.id));
    setTodayOverrides((current) => ({ ...current, [action.id]: nextIncluded }));
    try {
      await repository!.updateAction(action.id, action.revision, alreadyToday
        ? { today_override_on: todayDate, today_override_visible: false }
        : { today_override_on: todayDate, today_override_visible: true });
      await refresh();
    } catch {
      await refresh();
      setTodayOverrides((current) => {
        const next = { ...current };
        delete next[action.id];
        return next;
      });
      window.alert(alreadyToday ? "今日から外せませんでした。通信状態を確認して、もう一度お試しください。" : "今日に追加できませんでした。通信状態を確認して、もう一度お試しください。");
    } finally {
      pendingTodayActions.current.delete(action.id);
      setPendingTodayIds((current) => {
        const next = new Set(current);
        next.delete(action.id);
        return next;
      });
    }
  }
  async function moveAction(action: Action, destination: ActionDropDestination) {
    try {
      const movingAction = action.parent_id ? data.actions.find((item) => item.id === action.parent_id) || action : action;
      const movingRoots = actionTicketBlockRoots(open, movingAction);
      const sourceIds = movingRoots.map((item) => item.id);
      if (destination.kind === "lane") {
        if (movingAction.lane === destination.lane) return;
        setMovePreview({ sourceIds, targetId: null, placement: "after", targetLane: destination.lane });
        await repository!.changeActionLane({ id: movingAction.id, revision: movingAction.revision }, destination.lane);
        await refresh();
        setMovePreview(null);
        setMovingId(null);
        setSelectedId(null);
        return;
      }
      const rawTarget = data.actions.find((item) => item.id === destination.target_id);
      const target = rawTarget?.parent_id ? data.actions.find((item) => item.id === rawTarget.parent_id) || rawTarget : rawTarget;
      if (!target || target.id === movingAction.id) return;
      const normalizedDestination = { ...destination, target_id: target.id, target_revision: target.revision };
      if (normalizedDestination.kind === "child" && movingRoots.length > 1) throw new Error("同じチケットのアクションは、まとまりのまま子アクションにはできません");
      if (normalizedDestination.kind !== "child") setMovePreview({ sourceIds, targetId: target.id, placement: normalizedDestination.kind, targetLane: target.lane });
      if (normalizedDestination.kind !== "child" && movingAction.lane !== target.lane) {
        await repository!.changeActionLane(
          { id: movingAction.id, revision: movingAction.revision },
          target.lane,
          { id: target.id, revision: target.revision, placement: normalizedDestination.kind }
        );
      } else {
        await repository!.moveAction(movingAction, normalizedDestination);
      }
      await refresh();
      setMovePreview(null);
      setMovingId(null);
      setSelectedId(null);
    } catch (cause) {
      setMovePreview(null);
      setMovingId(null);
      await refresh();
      window.alert(cause instanceof Error ? cause.message : "アクションを移動できませんでした");
    }
  }
  return <section className="page"><PageHeader eyebrow="ACTIONS" title="アクション"><span className="count-pill">{open.length} 件</span></PageHeader>
    {movingId && <><MoveStatus action={open.find((action) => action.id === movingId)} cancel={() => setMovingId(null)} /><button type="button" className="mobile-move-cancel" onClick={() => setMovingId(null)}>移動をキャンセル</button></>}
    <div className="lanes">{(Object.keys(laneLabels) as ActionLane[]).map((id) => { const laneActions = actionsForLaneGroup(displayedOpen, id); return <section className="panel" key={id}><div className="panel-heading"><h2>{laneLabels[id]}</h2><span className="count-pill">{laneActions.length}</span></div><ActionList actions={orderActionsWithChildren(laneActions)} allActions={displayedOpen} tickets={data.tickets} ticketCatalog={data.ticketCatalog} online={online} refresh={refresh} crossLaneReorder ticketBlockReorder lane={id} selectedId={selectedId} movingId={movingId} highlightedId={highlightedId} pendingTodayIds={pendingTodayIds} onSelect={selectAction} onStartMove={startMove} onMoveAction={moveAction} onAddToToday={addActionToToday} todayActionIds={displayedTodayIds} onAddSubAction={beginSubAction} onDetailDirtyChange={setDetailDirty}/></section>; })}</div>
    {data.actions.some((x) => x.completed_at) && <details className="completed"><summary>完了済み ({data.actions.filter((x) => x.completed_at).length})</summary><ActionList actions={data.actions.filter((x) => x.completed_at).sort((a, b) => new Date(b.completed_at || 0).getTime() - new Date(a.completed_at || 0).getTime())} allActions={data.actions} tickets={data.tickets} ticketCatalog={data.ticketCatalog} online={online} refresh={refresh}/></details>}
    <button className="create-fab" type="button" aria-label="アクションを追加" title="アクションを追加" disabled={!online} onClick={() => { setCreatingForTicket(null); setCreating(true); }}>＋</button>
    {creating && <ActionCreator actions={open} tickets={data.tickets} catalog={data.ticketCatalog} fixedCatalogTicket={creatingForTicket || undefined} online={online} close={() => { setCreating(false); setCreatingForTicket(null); }} refresh={refresh} onCreated={highlight} />}
    {subActionParent && <ActionCreator actions={open} tickets={data.tickets} catalog={data.ticketCatalog} online={online} parent={subActionParent} close={() => setSubActionParent(null)} refresh={refresh} onCreated={highlight} />}
    {selectedAction && !movingId && <div className="modal-backdrop action-detail-modal" {...actionDetailSwipe} onMouseDown={(event) => { if (event.target === event.currentTarget) closeActionDetail(); }}><div className="action-detail-dialog"><header><div><p className="eyebrow">ACTION</p><h2>アクション詳細</h2></div><button type="button" className="icon-button" aria-label="閉じる" onClick={closeActionDetail}>×</button></header><ActionDetails action={selectedAction} allActions={data.actions} ticketCatalog={data.ticketCatalog} online={online} refresh={refresh} onSaved={() => { setSelectedId(null); setDetailDirty(false); }} onAddSubAction={!selectedAction.parent_id ? beginSubAction : undefined} onDirtyChange={setDetailDirty} todayActionIds={displayedTodayIds} pendingTodayIds={pendingTodayIds} onToggleRelatedToday={addActionToToday} /></div></div>}
  </section>;
}

function ActionCreator({ actions, tickets: _tickets, catalog = [], online, parent, fixedDate, fixedTicket, fixedCatalogTicket, close, refresh, onCreated }: { actions: Action[]; tickets: Ticket[]; catalog?: TicketCatalog[]; online: boolean; parent?: Action; fixedDate?: string; fixedTicket?: Ticket; fixedCatalogTicket?: TicketCatalog; close: () => void; refresh: () => Promise<void>; onCreated?: (id: string) => void }) {
  const inheritedTicketKey = parent?.ticket_key || fixedCatalogTicket?.ticket_key || "";
  const inheritedProject = parent?.project_name || fixedCatalogTicket?.project || fixedTicket?.project || "";
  const inheritedContext = parent?.context || fixedCatalogTicket?.title || fixedTicket?.title || "";
  const [text, setText] = useState("");
  const [note, setNote] = useState("");
  const [lane, setLane] = useState<ActionLane>(parent?.lane || "later");
  const [reminderOn, setReminderOn] = useState(fixedDate || "");
  const [reminderMode, setReminderMode] = useState<ReminderMode>("persistent");
  const [parentId, setParentId] = useState(parent?.id || "");
  const [ticketKey, setTicketKey] = useState(inheritedTicketKey);
  const [projectName, setProjectName] = useState(inheritedProject);
  const [context, setContext] = useState(inheritedContext);
  const [referenceUrl, setReferenceUrl] = useState(parent?.reference_url || fixedCatalogTicket?.reference_url || "");
  const [saving, setSaving] = useState(false);
  const reminderPickerRef = useRef<HTMLInputElement | null>(null);
  const parentCandidates = actions.filter((item) => !item.parent_id);
  const initialLane = parent?.lane || "later";
  const initialParentId = parent?.id || "";
  const initialTicketKey = inheritedTicketKey;
  const dirty = Boolean(text || note || (!fixedDate && reminderOn) || reminderMode !== "persistent" || lane !== initialLane || parentId !== initialParentId || ticketKey !== initialTicketKey);

  function requestClose() { if (!dirty || window.confirm("入力内容を破棄しますか？")) close(); }
  const swipeToClose = useSwipeRightToClose(requestClose, saving);
  function changeParent(nextId: string) {
    setParentId(nextId);
    const selectedParent = parentCandidates.find((item) => item.id === nextId);
    if (selectedParent) {
      setLane(selectedParent.lane);
      setTicketKey(selectedParent.ticket_key || "");
      setProjectName(selectedParent.project_name);
      setContext(selectedParent.context);
    }
  }
  function changeTicket(nextKey: string) {
    setTicketKey(nextKey);
    const selected = catalog.find((ticket) => ticket.ticket_key === nextKey);
    if (selected) {
      setProjectName(selected.project);
      setContext(selected.title);
      if (!referenceUrl && selected.reference_url) setReferenceUrl(selected.reference_url);
    }
  }

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === "Escape" && !saving) requestClose(); };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [dirty, saving]);

  async function add(event: FormEvent) {
    event.preventDefault();
    if (!online || !text.trim() || saving) return;
    setSaving(true);
    const draft = {
      text: text.trim(),
      note,
      lane,
      reminder_on: fixedDate || reminderOn || null,
      reminder_mode: reminderMode,
      today_override_on: fixedDate || null,
      today_override_visible: fixedDate ? true : null,
      parent_id: parentId || null,
      ticket_id: fixedTicket?.id || null,
      ticket_key: ticketKey || null,
      project_name: projectName.trim(),
      context: context.trim(),
      reference_url: referenceUrl.trim(),
    };
    close();
    try {
      const created = await repository!.createAction(draft);
      await refresh();
      onCreated?.(created.id);
    } catch (cause) {
      await refresh();
      window.alert(cause instanceof Error ? `アクションを追加できませんでした。\n${cause.message}` : "アクションを追加できませんでした。通信状態を確認して、もう一度お試しください。");
    }
  }

  const subAction = Boolean(parent);
  return <div className="modal-backdrop entry-modal" {...swipeToClose} onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) requestClose(); }}>
    <form className="action-editor" onSubmit={add}>
      <header><div><p className="eyebrow">{subAction ? "NEW SUB ACTION" : fixedDate ? "TODAY'S ACTION" : "NEW ACTION"}</p><h2>{subAction ? "新しいサブアクション" : fixedDate ? "今日のアクションを追加" : "新しいアクション"}</h2></div><button type="button" className="icon-button" onClick={requestClose} aria-label="閉じる" disabled={saving}>×</button></header>
      <label className="action-name-field">{subAction ? "サブアクション名" : "アクション名"}<input value={text} onChange={(event) => setText(event.target.value)} placeholder="やることを入力" required maxLength={500} autoFocus /></label>
      <div className="action-field-grid">
        <label>優先度<select value={lane} onChange={(event) => setLane(event.target.value as ActionLane)}>{Object.entries(laneLabels).map(([id, label]) => <option key={id} value={id}>{label}</option>)}</select></label>
        {fixedDate ? <label>リマインド<span className="fixed-field">{fixedDate}（今日）</span></label> : <label className="compact-date-field">リマインド（任意）<div className="action-date-with-reset"><div className="action-date-control"><span className="action-date-display">{reminderOn ? reminderOn.replaceAll("-", "/") : "未設定"}</span><input ref={reminderPickerRef} className="action-date-native" aria-label="リマインド日を選択" type="date" value={reminderOn} onChange={(event) => setReminderOn(event.target.value)} /></div><button type="button" className="date-reset-button" disabled={!reminderOn} onClick={() => { setReminderOn(""); setReminderMode("persistent"); }}>リセット</button></div></label>}
        <label>リマインド方法<select value={reminderMode} disabled={!fixedDate && !reminderOn} onChange={(event) => setReminderMode(event.target.value as ReminderMode)}><option value="persistent">完了まで毎日</option><option value="once">その日だけ</option></select></label>
        <label>親アクション{parent ? <span className="fixed-field">{parent.text}</span> : <select value={parentId} onChange={(event) => changeParent(event.target.value)}><option value="">単体アクション</option>{parentCandidates.map((item) => <option value={item.id} key={item.id}>{item.text}</option>)}</select>}</label>
        <div className="action-ticket-field"><span className="field-label">関連チケット</span><TicketPicker tickets={catalog} value={ticketKey} onChange={changeTicket} disabled={Boolean(parent || fixedCatalogTicket)} />{(parent || fixedCatalogTicket) && <small className="field-help">関連チケットは引き継がれています</small>}</div>
      </div>
      <p className="action-date-note">{fixedDate ? "今日から、完了するまで毎日リマインドします。" : "リマインドは未指定のまま登録できます。初期設定は「完了まで毎日」です。"}</p>
      <div className={`action-create-note-field action-note-field${note.trim() ? "" : " is-empty"}`}><div className="action-note-heading"><span>メモ（任意・Markdown）</span><small>{note.length}/{ACTION_NOTE_MAX_LENGTH}</small></div><small className="markdown-notation-help">{MARKDOWN_NOTATION_HELP}</small><Suspense fallback={<div className="memo-editor-loading action-note-loading">エディタを準備中…</div>}><MemoMarkdownEditor value={note} onChange={setNote} maxLength={ACTION_NOTE_MAX_LENGTH} ariaLabel="アクションのメモ"/></Suspense></div>
      <footer><button type="button" className="secondary" onClick={requestClose} disabled={saving}>キャンセル</button><button className="primary" disabled={!online || !text.trim() || saving}>{saving ? "追加中…" : subAction ? "サブアクションを追加" : "アクションを追加"}</button></footer>
    </form>
  </div>;
}

function TicketCatalogPage({ data, online, refresh }: PageProps) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<"active" | "waiting" | "closed" | "all">("active");
  const [sortBy, setSortBy] = useState<"updated-desc" | "updated-asc" | "title" | "status">("updated-desc");
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [creatingAction, setCreatingAction] = useState(false);
  const [readmeExpanded, setReadmeExpanded] = useState(false);
  const [mobileCatalog, setMobileCatalog] = useState(() => window.matchMedia("(max-width: 760px)").matches);
  useEffect(() => {
    const media = window.matchMedia("(max-width: 760px)");
    const update = () => setMobileCatalog(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  const filtered = useMemo(() => {
    const matchesQuery = (ticket: TicketCatalog) =>
      `${ticket.title} ${ticket.area} ${ticket.project} ${ticket.type}`.toLowerCase().includes(query.toLowerCase());
    const directVisible = data.ticketCatalog.filter((ticket) =>
      (status === "all" || ticket.status === status) && matchesQuery(ticket)
    );
    const contextParentKeys = new Set<string>();
    const byKey = new Map(data.ticketCatalog.map((ticket) => [ticket.ticket_key, ticket]));
    if (status === "active") {
      for (const ticket of directVisible) {
        let parentKey = ticket.parent_ticket_key || null;
        const seen = new Set<string>();
        while (parentKey && !seen.has(parentKey)) {
          seen.add(parentKey);
          const parent = byKey.get(parentKey);
          if (!parent) break;
          contextParentKeys.add(parent.ticket_key);
          parentKey = parent.parent_ticket_key || null;
        }
      }
    }
    const visible = status === "active"
      ? data.ticketCatalog.filter((ticket) => directVisible.some((item) => item.ticket_key === ticket.ticket_key) || contextParentKeys.has(ticket.ticket_key))
      : directVisible;
    const updatedTime = (ticket: TicketCatalog) => new Date(ticket.updated_at_local || ticket.synced_at || 0).getTime() || 0;
    const statusRank = (value: TicketCatalog["status"]) => ({ active: 0, waiting: 1, closed: 2 })[value] ?? 3;
    const compareTickets = (left: TicketCatalog, right: TicketCatalog) => {
      if (sortBy === "updated-asc") return updatedTime(left) - updatedTime(right) || left.title.localeCompare(right.title, "ja");
      if (sortBy === "title") return left.title.localeCompare(right.title, "ja");
      if (sortBy === "status") return statusRank(left.status) - statusRank(right.status) || updatedTime(right) - updatedTime(left);
      return updatedTime(right) - updatedTime(left) || left.title.localeCompare(right.title, "ja");
    };
    const base = [...visible].sort(compareTickets);
    const visibleKeys = new Set(base.map((ticket) => ticket.ticket_key));
    const childrenByParent = new Map<string, TicketCatalog[]>();
    for (const ticket of base) {
      if (!ticket.parent_ticket_key || !visibleKeys.has(ticket.parent_ticket_key)) continue;
      const children = childrenByParent.get(ticket.parent_ticket_key) || [];
      children.push(ticket);
      childrenByParent.set(ticket.parent_ticket_key, children);
    }
    for (const children of childrenByParent.values()) children.sort(compareTickets);

    const ordered: TicketCatalog[] = [];
    const added = new Set<string>();
    const appendWithChildren = (ticket: TicketCatalog) => {
      if (added.has(ticket.ticket_key)) return;
      ordered.push(ticket);
      added.add(ticket.ticket_key);
      for (const child of childrenByParent.get(ticket.ticket_key) || []) appendWithChildren(child);
    };

    for (const ticket of base) {
      if (ticket.parent_ticket_key && visibleKeys.has(ticket.parent_ticket_key)) continue;
      appendWithChildren(ticket);
    }
    for (const ticket of base) appendWithChildren(ticket);
    return ordered;
  }, [data.ticketCatalog, query, status, sortBy]);
  const selected = data.ticketCatalog.find((ticket) => ticket.ticket_key === selectedKey) || (!mobileCatalog ? filtered[0] : null) || null;
  useMobileBodyScrollLock(Boolean((mobileCatalog && selected) || creatingAction));
  useEffect(() => {
    if (!filtered.length) { setSelectedKey(null); return; }
    if (selectedKey && !filtered.some((ticket) => ticket.ticket_key === selectedKey)) setSelectedKey(null);
    if (!mobileCatalog && !selectedKey) setSelectedKey(filtered[0].ticket_key);
  }, [filtered, selectedKey, mobileCatalog]);
  useEffect(() => { setReadmeExpanded(false); }, [selected?.ticket_key]);
  const relatedActions = selected ? orderActionsWithChildren(data.actions.filter((action) => action.ticket_key === selected.ticket_key)) : [];
  const catalogByKey = new Map(data.ticketCatalog.map((ticket) => [ticket.ticket_key, ticket]));
  const selectedParent = selected?.parent_ticket_key ? catalogByKey.get(selected.parent_ticket_key) || null : null;
  const selectedChildren = selected ? data.ticketCatalog.filter((ticket) => ticket.parent_ticket_key === selected.ticket_key) : [];
  const selectedRelated = selected
    ? (selected.related_ticket_keys || []).map((key) => catalogByKey.get(key)).filter((ticket): ticket is TicketCatalog => Boolean(ticket))
    : [];
  function selectRelatedTicket(key: string) {
    const target = catalogByKey.get(key);
    if (!target) return;
    setQuery("");
    setStatus(target.status);
    setSelectedKey(key);
  }
  const counts = {
    active: data.ticketCatalog.filter((ticket) => ticket.status === "active").length,
    waiting: data.ticketCatalog.filter((ticket) => ticket.status === "waiting").length,
    closed: data.ticketCatalog.filter((ticket) => ticket.status === "closed").length,
  };
  const ticketDetailSwipe = useSwipeRightToClose(() => setSelectedKey(null), !mobileCatalog || !selected);
  return <section className="page catalog-page"><PageHeader eyebrow="TICKETS" title="チケット"><span className="count-pill">{filtered.length} / {data.ticketCatalog.length} 件</span></PageHeader>
    <div className="catalog-layout">
      <aside className="panel catalog-list">
        <div className="catalog-controls"><input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="チケットを検索"/><div className="catalog-status-tabs" role="group" aria-label="チケット状態">{([["active", "Active", counts.active], ["waiting", "Waiting", counts.waiting], ["closed", "Closed", counts.closed], ["all", "すべて", data.ticketCatalog.length]] as const).map(([id, label, count]) => <button type="button" key={id} className={status === id ? "active" : ""} onClick={() => setStatus(id)}><span>{label}</span><strong>{count}</strong></button>)}</div><label className="catalog-sort">並び順<select value={sortBy} onChange={(event) => setSortBy(event.target.value as typeof sortBy)}><option value="updated-desc">更新が新しい順</option><option value="updated-asc">更新が古い順</option><option value="title">名前順</option><option value="status">状態順</option></select></label></div>
        <div className="catalog-items">{filtered.map((ticket) => {
          const validParent = Boolean(ticket.parent_ticket_key && data.ticketCatalog.some((parent) => parent.ticket_key === ticket.parent_ticket_key));
          const contextParent = status === "active" && ticket.status !== "active" && data.ticketCatalog.some((child) => child.parent_ticket_key === ticket.ticket_key && child.status === "active");
          return <button key={ticket.ticket_key} className={`${selected?.ticket_key === ticket.ticket_key ? "selected" : ""} ${validParent ? "sub-ticket" : ""} ${contextParent ? "context-parent" : ""}`.trim()} onClick={() => setSelectedKey(ticket.ticket_key)}><strong>{ticket.title}</strong><small><span className={`status ${ticket.status}`}>{ticket.status}</span>{ticket.project || "未設定"}</small></button>;
        })}{!filtered.length && <Empty text="表示できるチケットはありません。"/>}</div>
      </aside>
      {selected && <article className={`panel catalog-detail ${mobileCatalog ? "catalog-detail-sheet" : ""}`} {...(mobileCatalog ? ticketDetailSwipe : {})}>
        <header><div><p className="eyebrow">TICKET CATALOG</p><h2>{selected.title}</h2></div><div className="catalog-detail-header-actions"><span className={`status ${selected.status}`}>{selected.status}</span>{mobileCatalog && <button type="button" className="icon-button catalog-detail-close" aria-label="閉じる" onClick={() => setSelectedKey(null)}>×</button>}</div></header>
        <dl><div><dt>プロジェクト</dt><dd>{selected.project || "未設定"}</dd></div><div><dt>領域</dt><dd>{selected.area || "未設定"}</dd></div><div><dt>種別</dt><dd>{selected.type || "未設定"}</dd></div><div><dt>最終同期</dt><dd>{new Date(selected.synced_at).toLocaleString("ja-JP")}</dd></div></dl>
        <section className="catalog-relations">
          <div className="panel-heading"><div><p className="eyebrow">RELATIONS</p><h3>関連チケット</h3></div><span className="count-pill">{(selectedParent ? 1 : 0) + selectedChildren.length + selectedRelated.length}</span></div>
          <div className="catalog-relation-groups">
            <CatalogRelationGroup title="親" tickets={selectedParent ? [selectedParent] : []} select={selectRelatedTicket}/>
            <CatalogRelationGroup title="子" tickets={selectedChildren} select={selectRelatedTicket}/>
            <CatalogRelationGroup title="関連" tickets={selectedRelated} select={selectRelatedTicket}/>
          </div>
        </section>
        <section className="catalog-readme">
          <div className="panel-heading"><div><p className="eyebrow">README</p><h3>README.md</h3></div><div className="catalog-readme-controls"><span className="catalog-readonly-label">閲覧のみ</span><button type="button" className="secondary catalog-readme-toggle" aria-expanded={readmeExpanded} onClick={() => setReadmeExpanded((current) => !current)}>{readmeExpanded ? "閉じる" : "READMEを見る"}</button></div></div>
          {readmeExpanded && <MarkdownPreview markdown={selected.readme_markdown || ""}/>}
        </section>
        <section className="catalog-actions"><div className="panel-heading"><div><p className="eyebrow">RELATED ACTIONS</p><h3>関連アクション</h3></div><button className="primary" disabled={!online} onClick={() => setCreatingAction(true)}>＋ アクション追加</button></div>
          <ActionList actions={relatedActions} allActions={data.actions} tickets={data.tickets} ticketCatalog={data.ticketCatalog} online={online} refresh={refresh}/>
        </section>
        {selected.reference_url && <a className="reference-link" href={selected.reference_url} target="_blank" rel="noreferrer">参照先を開く ↗</a>}
      </article>}
    </div>
    {creatingAction && selected && <ActionCreator actions={data.actions.filter((action) => !action.completed_at)} tickets={data.tickets} catalog={data.ticketCatalog} fixedCatalogTicket={selected} online={online} close={() => setCreatingAction(false)} refresh={refresh}/>}
  </section>;
}

function CatalogRelationGroup({ title, tickets, select }: { title: string; tickets: TicketCatalog[]; select: (key: string) => void }) {
  return <div className="catalog-relation-group"><span>{title}</span>{tickets.length ? <div>{tickets.map((ticket) => <button type="button" key={ticket.ticket_key} onClick={() => select(ticket.ticket_key)}><strong>{ticket.title}</strong><small><span className={`status ${ticket.status}`}>{ticket.status}</span>{ticket.project || "未設定"}</small></button>)}</div> : <small>なし</small>}</div>;
}

function TicketsPage({ data, online, refresh }: PageProps) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("active");
  const [area, setArea] = useState("all");
  const [project, setProject] = useState("all");
  const [type, setType] = useState("all");
  const [sort, setSort] = useState("hierarchy");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mobileDetail, setMobileDetail] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [fileCreating, setFileCreating] = useState(false);
  const [actionCreating, setActionCreating] = useState(false);
  const [viewingFileId, setViewingFileId] = useState<string | null>(null);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  useMobileBodyScrollLock(Boolean(mobileDetail || creating || editing || fileCreating || viewingFileId || actionCreating));
  const areas = [...new Set(data.tickets.map((ticket) => ticket.area).filter(Boolean))];
  const projects = [...new Set(data.tickets.map((ticket) => ticket.project).filter(Boolean))];
  const types = [...new Set(data.tickets.map((ticket) => ticket.type).filter(Boolean))];
  const filtered = useMemo(() => data.tickets.filter((ticket) =>
    (status === "all" || ticket.status === status) && (area === "all" || ticket.area === area) &&
    (project === "all" || ticket.project === project) && (type === "all" || ticket.type === type) &&
    `${ticket.title} ${ticket.purpose} ${ticket.conclusion} ${ticket.area} ${ticket.project} ${ticket.type}`.toLowerCase().includes(query.toLowerCase())
  ), [data.tickets, query, status, area, project, type]);
  const list = useMemo(() => orderTickets(filtered, sort), [filtered, sort]);
  const selected = data.tickets.find((ticket) => ticket.id === selectedId) || null;
  const viewingFile = data.ticketFiles.find((file) => file.id === viewingFileId) || null;
  useEffect(() => {
    if (!list.length) { setSelectedId(null); setMobileDetail(false); return; }
    if (!selectedId || !list.some((ticket) => ticket.id === selectedId)) setSelectedId(list[0].id);
  }, [list, selectedId]);
  function selectTicket(id: string, showDetail = true) { setSelectedId(id); if (showDetail) setMobileDetail(true); }
  function highlight(id: string) { setHighlightedId(id); setSelectedId(id); window.setTimeout(() => setHighlightedId((current) => current === id ? null : current), 1800); }
  const counts = { active: data.tickets.filter((ticket) => ticket.status === "active").length, waiting: data.tickets.filter((ticket) => ticket.status === "waiting").length, closed: data.tickets.filter((ticket) => ticket.status === "closed").length };
  return <section className="page tickets-page"><PageHeader eyebrow="TICKETS" title="チケット"><span className="count-pill">{filtered.length} / {data.tickets.length} 件</span></PageHeader>
    <div className={`ticket-workbench ${mobileDetail ? "mobile-detail" : ""}`}>
      <aside className="ticket-browser panel">
        <div className="ticket-filter-stack">
          <label>検索<input type="search" placeholder="タイトル、本文、関連情報" value={query} onChange={(event) => setQuery(event.target.value)}/></label>
          <div className="ticket-filter-grid"><label>状態<select value={status} onChange={(event) => setStatus(event.target.value)}><option value="active">Active</option><option value="waiting">Waiting</option><option value="closed">Closed</option><option value="all">すべて</option></select></label><label>領域<select value={area} onChange={(event) => setArea(event.target.value)}><option value="all">すべて</option>{areas.map((value) => <option key={value}>{value}</option>)}</select></label><label>プロジェクト<select value={project} onChange={(event) => setProject(event.target.value)}><option value="all">すべて</option>{projects.map((value) => <option key={value}>{value}</option>)}</select></label><label>種別<select value={type} onChange={(event) => setType(event.target.value)}><option value="all">すべて</option>{types.map((value) => <option key={value}>{value}</option>)}</select></label></div>
        </div>
        <div className="ticket-stats"><button className={status === "active" ? "active" : ""} onClick={() => setStatus("active")}><span>Active</span><strong>{counts.active}</strong></button><button className={status === "waiting" ? "active" : ""} onClick={() => setStatus("waiting")}><span>Waiting</span><strong>{counts.waiting}</strong></button><button className={status === "closed" ? "active" : ""} onClick={() => setStatus("closed")}><span>Closed</span><strong>{counts.closed}</strong></button></div>
        <div className="ticket-list-heading"><div><strong>{status === "all" ? "すべて" : status[0].toUpperCase() + status.slice(1)}</strong><span>{filtered.length}件表示</span></div><label>並び順<select value={sort} onChange={(event) => setSort(event.target.value)}><option value="hierarchy">親子関係順</option><option value="updated">最近更新した順</option><option value="title">名前順</option></select></label></div>
        <div className="ticket-list">{list.map((ticket) => { const childCount = data.tickets.filter((item) => item.parent_id === ticket.id).length; return <button key={ticket.id} className={`${selectedId === ticket.id ? "selected" : ""} ${ticket.parent_id ? "child" : ""} ${highlightedId === ticket.id ? "just-created" : ""}`} onClick={() => selectTicket(ticket.id)}><span className="ticket-list-title">{ticket.parent_id && <i>↳</i>}<strong>{ticket.title}</strong>{childCount > 0 && <em>子{childCount}</em>}</span><span className="ticket-list-meta"><span className={`status ${ticket.status}`}>{ticket.status}</span><span>{ticket.project || "未設定"}</span><time>{formatUpdated(ticket.updated_at)}</time></span></button>; })}{!list.length && <Empty text="条件に合うチケットはありません。"/>}</div>
      </aside>
      {selected ? <TicketDetail ticket={selected} data={data} online={online} refresh={refresh} back={() => setMobileDetail(false)} navigate={selectTicket} edit={() => setEditing(true)} addFile={() => setFileCreating(true)} openFile={setViewingFileId} addAction={() => setActionCreating(true)} /> : <section className="ticket-detail panel"><Empty text="表示するチケットを選択してください。"/></section>}
    </div>
    <button className="create-fab" type="button" aria-label="チケットを追加" title="チケットを追加" disabled={!online} onClick={() => setCreating(true)}>＋</button>
    {creating && <TicketEditor ticket={null} tickets={data.tickets} online={online} close={() => setCreating(false)} refresh={refresh} onCreated={highlight}/>} 
    {editing && selected && <TicketEditor ticket={selected} tickets={data.tickets} online={online} close={() => setEditing(false)} refresh={refresh}/>} 
    {fileCreating && selected && <TicketFileEditor ticket={selected} file={null} online={online} close={() => setFileCreating(false)} refresh={refresh}/>} 
    {viewingFile && selected && <TicketFileEditor ticket={selected} file={viewingFile} online={online} close={() => setViewingFileId(null)} refresh={refresh}/>} 
    {actionCreating && selected && <ActionCreator actions={data.actions.filter((action) => !action.completed_at)} tickets={data.tickets} catalog={data.ticketCatalog} online={online} fixedTicket={selected} close={() => setActionCreating(false)} refresh={refresh}/>} 
  </section>;
}

function orderTickets(tickets: Ticket[], sort: string): Ticket[] {
  const base = [...tickets].sort((a, b) => sort === "title" ? a.title.localeCompare(b.title, "ja") : new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
  if (sort !== "hierarchy") return base;
  const ids = new Set(base.map((ticket) => ticket.id));
  const result: Ticket[] = [];
  const seen = new Set<string>();
  function append(ticket: Ticket) { if (seen.has(ticket.id)) return; seen.add(ticket.id); result.push(ticket); base.filter((item) => item.parent_id === ticket.id).forEach(append); }
  base.filter((ticket) => !ticket.parent_id || !ids.has(ticket.parent_id)).forEach(append);
  base.forEach(append);
  return result;
}

function formatUpdated(value: string): string { return new Date(value).toLocaleString("ja-JP", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }); }
function formatBytes(value: number): string { if (value < 1024) return `${value} B`; if (value < 1024 ** 2) return `${Math.ceil(value / 1024)} KB`; return `${(value / 1024 ** 2).toFixed(1)} MB`; }
function fileStorageLabel(file: TicketFile): string {
  if (file.kind === "page") return "編集可能";
  if (file.storage_mode === "local_only") return "ローカルのみ";
  if (file.cloud_status === "available") return file.storage_mode === "synced" ? "R2同期済み" : "R2保存済み";
  if (file.cloud_status === "uploading") return "アップロード中";
  if (file.cloud_status === "error") return "保存エラー";
  return "クラウド保存待ち";
}

function TicketDetail({ ticket, data, online, refresh, back, navigate, edit, addFile, openFile, addAction }: { ticket: Ticket; data: DashboardData; online: boolean; refresh: () => Promise<void>; back: () => void; navigate: (id: string) => void; edit: () => void; addFile: () => void; openFile: (id: string) => void; addAction: () => void }) {
  const [localFiles, setLocalFiles] = useState<LocalTicketFile[]>([]);
  const [localConnected, setLocalConnected] = useState(hasLocalBridgeConnection());
  const [pairing, setPairing] = useState(false);
  const [localMessage, setLocalMessage] = useState("");
  const parent = data.tickets.find((item) => item.id === ticket.parent_id);
  const children = data.tickets.filter((item) => item.parent_id === ticket.id);
  const relatedIds = data.ticketRelations.flatMap((relation) => relation.source_ticket_id === ticket.id ? [relation.target_ticket_id] : relation.target_ticket_id === ticket.id ? [relation.source_ticket_id] : []);
  const related = data.tickets.filter((item) => relatedIds.includes(item.id));
  const files = data.ticketFiles.filter((file) => file.ticket_id === ticket.id);
  const cloudPaths = new Set(files.map((file) => file.relative_path.toLowerCase()));
  const localOnlyFiles = localFiles.filter((file) => !cloudPaths.has(file.relativePath.toLowerCase()));
  const localPaths = new Set(localFiles.map((file) => file.relativePath.toLowerCase()));
  const actions = orderActionsWithChildren(data.actions.filter((action) => action.ticket_id === ticket.id));
  const loadLocalFiles = useCallback(async () => {
    if (!hasLocalBridgeConnection()) { setLocalConnected(false); setLocalFiles([]); return; }
    try { setLocalFiles(await listLocalTicketFiles(ticket.folder_name)); setLocalConnected(true); setLocalMessage(""); }
    catch (error) { setLocalConnected(false); setLocalFiles([]); setLocalMessage(error instanceof Error ? error.message : "このPCと接続できません。"); }
  }, [ticket.folder_name]);
  useEffect(() => { void loadLocalFiles(); }, [loadLocalFiles]);
  async function openLocal(file: LocalTicketFile) { try { await openLocalTicketFile(ticket.folder_name, file.relativePath); setLocalMessage(""); } catch (error) { setLocalMessage(error instanceof Error ? error.message : "開けませんでした。"); } }
  async function setStatus(next: Ticket["status"]) { if (next === ticket.status) return; const draft = { ...ticket, status: next }; await repository!.updateTicket(ticket.id, ticket.revision, { status: next, readme_markdown: mergeTicketFieldsIntoMarkdown(ticket.readme_markdown, draft) }); await refresh(); }
  async function toggleAction(action: Action) { await repository!.updateAction(action.id, action.revision, { completed: !action.completed_at }); await refresh(); }
  return <article className="ticket-detail panel">
    <header className="ticket-detail-header"><button className="mobile-back" onClick={back}>← 一覧へ</button><div className="ticket-title-row"><div><p className="eyebrow">TICKET DETAIL</p><h2>{ticket.title}</h2></div><button className="secondary" onClick={edit}>編集</button></div><div className="ticket-status-switch">{(["active", "waiting", "closed"] as Ticket["status"][]).map((value) => <button key={value} className={ticket.status === value ? "active" : ""} disabled={!online} onClick={() => setStatus(value)}>{value[0].toUpperCase() + value.slice(1)}</button>)}</div><div className="ticket-tags"><span>{ticket.area || "領域未設定"}</span><span>{ticket.project || "プロジェクト未設定"}</span><span>{ticket.type || "種別未設定"}</span><time>更新 {formatUpdated(ticket.updated_at)}</time></div></header>
    <section className="ticket-detail-section"><div className="section-title"><h3>チケット情報</h3><span>親 {parent ? 1 : 0}・子 {children.length}・関連 {related.length}</span></div><dl className="ticket-facts"><div><dt>目的</dt><dd>{ticket.purpose || "未設定"}</dd></div><div><dt>直近の結論</dt><dd>{ticket.conclusion || "未設定"}</dd></div><div><dt>次にやること</dt><dd>{ticket.next_action || "未設定"}</dd></div></dl><div className="ticket-relations"><RelationGroup title="親チケット" tickets={parent ? [parent] : []} navigate={navigate}/><RelationGroup title="子チケット" tickets={children} navigate={navigate}/><RelationGroup title="関連チケット" tickets={related} navigate={navigate}/></div></section>
    <section className="ticket-detail-section"><div className="section-title"><div><h3>チケット内ファイル</h3><span>READMEを含む {files.length + localOnlyFiles.length + 1}件{localConnected ? " ・ PC接続中" : ""}</span></div><div className="section-actions">{!localConnected && <button className="secondary" onClick={() => setPairing(true)}>このPCと接続</button>}<button className="secondary" disabled={!online} onClick={addFile}>＋ ファイル追加</button></div></div>{localMessage && <p className="local-bridge-message">{localMessage}</p>}<div className="ticket-files"><button className="ticket-file-row readme" onClick={() => document.getElementById(`readme-${ticket.id}`)?.scrollIntoView({ behavior: "smooth" })}><span className="file-icon">M↓</span><span><strong>README.md</strong><small>Markdown・DB正本・編集可能</small></span><em>固定</em></button>{files.map((file) => <button className="ticket-file-row" key={file.id} onClick={() => openFile(file.id)}><span className="file-icon">{file.kind === "page" ? "M↓" : file.mime_type === "application/pdf" ? "PDF" : "FILE"}</span><span><strong>{file.display_name}</strong><small>{file.mime_type}・{formatBytes(file.byte_size)}・{fileStorageLabel(file)}{localPaths.has(file.relative_path.toLowerCase()) ? "・このPCにあり" : ""}</small></span><em>›</em></button>)}{localOnlyFiles.map((file) => <button className="ticket-file-row local-file" key={`local:${file.relativePath}`} onClick={() => openLocal(file)}><span className="file-icon">PC</span><span><strong>{file.displayName}</strong><small>{formatBytes(file.byteSize)}・このPCのみ・クラウド未送信</small></span><em>開く</em></button>)}</div></section>
    <section className="ticket-detail-section"><div className="section-title"><div><h3>関連アクション</h3><span>{actions.filter((action) => !action.completed_at).length}件の未完了</span></div><button className="secondary" disabled={!online} onClick={addAction}>＋ アクション追加</button></div><div className="ticket-actions">{actions.map((action) => <label key={action.id} className={action.parent_id ? "child" : ""}><input type="checkbox" checked={Boolean(action.completed_at)} disabled={!online} onChange={() => toggleAction(action)}/><span><strong>{action.text}</strong><small>{action.parent_id ? "サブアクション · " : ""}{laneLabels[action.lane]} · {action.reminder_on || "リマインド未設定"}</small></span></label>)}{!actions.length && <Empty text="関連アクションはありません。"/>}</div></section>
    <section className="ticket-readme" id={`readme-${ticket.id}`}><div className="section-title"><div><p className="eyebrow">README</p><h3>README.md</h3></div><button className="secondary" onClick={edit}>編集</button></div><MarkdownPreview markdown={ticket.readme_markdown}/></section>
    {pairing && <LocalBridgePairer close={() => setPairing(false)} connected={async () => { await loadLocalFiles(); setPairing(false); }}/>} 
  </article>;
}

function LocalBridgePairer({ close, connected }: { close: () => void; connected: () => Promise<void> }) {
  const [code, setCode] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  async function submit(event: FormEvent) { event.preventDefault(); setSaving(true); setMessage(""); try { await pairLocalBridge(code); await connected(); } catch (error) { setMessage(error instanceof Error ? error.message : "接続できません。"); } finally { setSaving(false); } }
  return <div className="modal-backdrop entry-modal"><form className="editor-card narrow" onSubmit={submit}><header><div><p className="eyebrow">LOCAL FILES</p><h2>このPCと接続</h2></div><button type="button" className="icon-button" onClick={close}>×</button></header><p className="local-pair-help">PCでDaily Desk Syncを起動し、画面に表示された接続コードを入力してください。</p><label>接続コード<input value={code} onChange={(event) => setCode(event.target.value.toUpperCase())} autoComplete="off" maxLength={10} required autoFocus/></label>{message && <p className="form-error">{message}</p>}<footer><button type="button" className="secondary" onClick={close}>キャンセル</button><button className="primary" disabled={saving || !code.trim()}>{saving ? "接続中…" : "接続"}</button></footer></form></div>;
}

function RelationGroup({ title, tickets, navigate }: { title: string; tickets: Ticket[]; navigate: (id: string) => void }) { return <div><span>{title}</span>{tickets.length ? <div>{tickets.map((ticket) => <button key={ticket.id} onClick={() => navigate(ticket.id)}>{ticket.title}</button>)}</div> : <small>なし</small>}</div>; }

function MarkdownPreview({ markdown }: { markdown: string }) {
  const visibleMarkdown = markdown.replace(/^---\r?\n[\s\S]*?\r?\n---\r?\n?/, "");
  if (!visibleMarkdown.trim()) return <Empty text="READMEはまだありません。"/>;
  return <div className="markdown-preview">{visibleMarkdown.split(/\r?\n/).map((line, index) => {
    const heading = /^(#{1,3})\s+(.*)$/.exec(line); if (heading) { const level = heading[1].length; return level === 1 ? <h1 key={index}>{heading[2]}</h1> : level === 2 ? <h2 key={index}>{heading[2]}</h2> : <h3 key={index}>{heading[2]}</h3>; }
    const task = /^- \[([ xX])\]\s+(.*)$/.exec(line); if (task) return <p className="markdown-task" key={index}><span>{task[1] === " " ? "□" : "✓"}</span>{task[2]}</p>;
    const bullet = /^-\s+(.*)$/.exec(line); if (bullet) return <p className="markdown-bullet" key={index}>• {bullet[1]}</p>;
    return line ? <p key={index}>{line}</p> : <div className="markdown-space" key={index}/>;
  })}</div>;
}

function TicketFileEditor({ ticket, file, online, close, refresh }: { ticket: Ticket; file: TicketFile | null; online: boolean; close: () => void; refresh: () => Promise<void> }) {
  const unsupported = file?.kind === "media";
  const [entryType, setEntryType] = useState<"markdown" | "upload">("markdown");
  const [title, setTitle] = useState(file?.display_name || "");
  const [path, setPath] = useState(file?.relative_path || "");
  const [content, setContent] = useState(file?.content || "");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const dirty = selectedFile !== null || title !== (file?.display_name || "") || path !== (file?.relative_path || "") || content !== (file?.content || "");
  function requestClose() { if (!dirty || window.confirm("入力内容を破棄しますか？")) close(); }
  async function save(event: FormEvent) { event.preventDefault(); if (!online || saving) return; setMessage(""); setSaving(true); try { if (!file && entryType === "upload") { if (!selectedFile) return; await repository!.uploadTicketFile(ticket.id, selectedFile); } else { if (!title.trim()) return; const resolvedPath = (path.trim() || `${title.trim().replace(/[\\/:*?"<>|]/g, "-")}.md`).replace(/\.md$/i, "") + ".md"; if (file) await repository!.updateTicketFile(file.id, file.revision, { ticket_id: ticket.id, relative_path: resolvedPath, title: title.trim(), content }); else await repository!.createTicketFile({ ticket_id: ticket.id, relative_path: resolvedPath, title: title.trim(), content }); } await refresh(); close(); } catch (error) { setMessage(error instanceof Error ? error.message : "保存できませんでした。"); } finally { setSaving(false); } }
  async function remove() { if (!file || !window.confirm(`「${file.display_name}」を削除しますか？`)) return; await repository!.deleteTicketFile(file); await refresh(); close(); }
  async function download() { if (!file) return; setSaving(true); setMessage(""); try { const blob = await repository!.downloadTicketFile(file); const url = URL.createObjectURL(blob); const anchor = document.createElement("a"); anchor.href = url; anchor.download = file.display_name; anchor.click(); window.setTimeout(() => URL.revokeObjectURL(url), 1000); } catch (error) { setMessage(error instanceof Error ? error.message : "取得できませんでした。"); } finally { setSaving(false); } }
  if (unsupported && file) return <div className="modal-backdrop"><section className="file-viewer-card"><header><div><p className="eyebrow">TICKET FILE</p><h2>{file.display_name}</h2></div><button className="icon-button" onClick={close}>×</button></header><div className="unsupported-file"><span className="unsupported-icon">{file.mime_type === "application/pdf" ? "PDF" : "FILE"}</span><h3>このメディアタイプは現在はプレビュー非対応です</h3><p>{file.cloud_status === "available" ? "ファイル本体は非公開R2に保存されています。ダウンロードして既定のアプリで開けます。" : "ローカル側ではDaily Desk Syncが所在を管理します。クラウドに本体はありません。"}</p><dl><div><dt>種類</dt><dd>{file.mime_type}</dd></div><div><dt>容量</dt><dd>{formatBytes(file.byte_size)}</dd></div><div><dt>保存状態</dt><dd>{fileStorageLabel(file)}</dd></div><div><dt>相対パス</dt><dd>{file.relative_path}</dd></div></dl>{message && <p className="error">{message}</p>}</div><footer><button className="danger" disabled={!online || saving} onClick={remove}>削除</button>{file.cloud_status === "available" && <button className="primary" disabled={!online || saving} onClick={download}>{saving ? "取得中…" : "ダウンロード"}</button>}<button className="secondary" onClick={close}>閉じる</button></footer></section></div>;
  return <div className="modal-backdrop entry-modal" onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) requestClose(); }}><form className="file-viewer-card file-editor-card" onSubmit={save}><header><div><p className="eyebrow">{file ? "EDIT TICKET FILE" : "NEW TICKET FILE"}</p><h2>{file ? file.display_name : "チケット内ファイルを追加"}</h2></div><button type="button" className="icon-button" onClick={requestClose}>×</button></header>{!file && <div className="file-type-switch"><button type="button" className={entryType === "markdown" ? "active" : ""} onClick={() => setEntryType("markdown")}>Markdownページ</button><button type="button" className={entryType === "upload" ? "active" : ""} onClick={() => setEntryType("upload")}>ファイルを選択</button></div>}{entryType === "upload" && !file ? <div className="upload-field"><label>ファイル<input type="file" onChange={(event) => setSelectedFile(event.target.files?.[0] || null)} /></label><p>初版は1ファイル100MBまで。非公開R2へ保存し、プレビュー非対応形式もダウンロードできます。</p>{selectedFile && <dl><div><dt>名前</dt><dd>{selectedFile.name}</dd></div><div><dt>容量</dt><dd>{formatBytes(selectedFile.size)}</dd></div></dl>}</div> : <><label>表示名<input value={title} onChange={(event) => setTitle(event.target.value)} required autoFocus={!file}/></label><label>ファイル名<input value={path} onChange={(event) => setPath(event.target.value)} placeholder="例: design-note.md"/></label><label>Markdown<textarea className="code-editor" value={content} onChange={(event) => setContent(event.target.value)} rows={16}/></label></>}{message && <p className="form-error">{message}</p>}<footer>{file && <button type="button" className="danger" disabled={!online || saving} onClick={remove}>削除</button>}<button type="button" className="secondary" onClick={requestClose}>キャンセル</button><button className="primary" disabled={!online || saving || (entryType === "upload" ? !selectedFile : !title.trim())}>{saving ? "保存中…" : entryType === "upload" ? "R2へ保存" : "保存"}</button></footer></form></div>;
}

function TicketEditor({ ticket, tickets, online, close, refresh, onCreated }: { ticket: Ticket | null; tickets: Ticket[]; online: boolean; close: () => void; refresh: () => Promise<void>; onCreated?: (id: string) => void }) {
  const [title, setTitle] = useState(ticket?.title || ""); const [status, setStatus] = useState<Ticket["status"]>(ticket?.status || "active"); const [area, setArea] = useState(ticket?.area || "ops"); const [project, setProject] = useState(ticket?.project || "作業環境整備"); const [type, setType] = useState(ticket?.type || "app"); const [parentId, setParentId] = useState(ticket?.parent_id || ""); const [purpose, setPurpose] = useState(ticket?.purpose || ""); const [conclusion, setConclusion] = useState(ticket?.conclusion || ""); const [next, setNext] = useState(ticket?.next_action || ""); const [markdown, setMarkdown] = useState(ticket?.readme_markdown || "");
  const [saving, setSaving] = useState(false);
  const dirty = title !== (ticket?.title || "") || status !== (ticket?.status || "active") || area !== (ticket?.area || "ops") || project !== (ticket?.project || "作業環境整備") || type !== (ticket?.type || "app") || parentId !== (ticket?.parent_id || "") || purpose !== (ticket?.purpose || "") || conclusion !== (ticket?.conclusion || "") || next !== (ticket?.next_action || "") || markdown !== (ticket?.readme_markdown || "");
  function requestClose() { if (!dirty || window.confirm("入力内容を破棄しますか？")) close(); }
  const swipeToClose = useSwipeRightToClose(requestClose, saving);
  useEffect(() => { const closeOnEscape = (event: KeyboardEvent) => { if (event.key === "Escape" && !saving) requestClose(); }; window.addEventListener("keydown", closeOnEscape); return () => window.removeEventListener("keydown", closeOnEscape); }, [dirty, saving]);
  async function save(e: FormEvent) { e.preventDefault(); if (saving) return; setSaving(true); try { const base = { title: title.trim(), parent_id: parentId || null, folder_name: ticket?.folder_name || ticketFolderName(title), status, area: area.trim(), project: project.trim(), type: type.trim(), purpose, conclusion, next_action: next, readme_markdown: markdown || `# ${title.trim()}\n` }; const draft = { ...base, readme_markdown: mergeTicketFieldsIntoMarkdown(base.readme_markdown, base) }; if (ticket) await repository!.updateTicket(ticket.id, ticket.revision, draft); else { const created = await repository!.createTicket(draft); onCreated?.(created.id); } await refresh(); close(); } finally { setSaving(false); } }
  async function remove() { if (!ticket || !window.confirm(`「${ticket.title}」を削除しますか？ 30日間はゴミ箱に残ります。`)) return; await repository!.trash("ticket", ticket); await refresh(); close(); }
  return <div className="modal-backdrop entry-modal" {...swipeToClose} onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) requestClose(); }}><form className="editor-card ticket-editor-card" onSubmit={save}><header><div><p className="eyebrow">{ticket ? "EDIT TICKET" : "NEW TICKET"}</p><h2>{ticket?.title || "新しいチケット"}</h2></div><button type="button" className="icon-button" onClick={requestClose} aria-label="閉じる" disabled={saving}>×</button></header><label className="wide-field">チケット名<input value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={200} autoFocus={!ticket}/></label><label>状態<select value={status} onChange={(e) => setStatus(e.target.value as Ticket["status"])}><option value="active">active</option><option value="waiting">waiting</option><option value="closed">closed</option></select></label><label>親チケット<select value={parentId} onChange={(e) => setParentId(e.target.value)}><option value="">親なし</option>{tickets.filter((item) => item.id !== ticket?.id && item.parent_id !== ticket?.id).map((item) => <option key={item.id} value={item.id}>{item.title}</option>)}</select></label><label>領域<input value={area} onChange={(e) => setArea(e.target.value)}/></label><label>プロジェクト<input value={project} onChange={(e) => setProject(e.target.value)}/></label><label>種別<input value={type} onChange={(e) => setType(e.target.value)}/></label><label className="wide-field">目的<textarea value={purpose} onChange={(e) => setPurpose(e.target.value)} rows={3}/></label><label className="wide-field">直近の結論<textarea value={conclusion} onChange={(e) => setConclusion(e.target.value)} rows={3}/></label><label className="wide-field">次にやること<textarea value={next} onChange={(e) => setNext(e.target.value)} rows={3}/></label><label className="wide-field">README.md<textarea className="code-editor" value={markdown} onChange={(e) => setMarkdown(e.target.value)} rows={12}/></label><footer>{ticket && <button type="button" className="danger" disabled={!online || saving} onClick={remove}>削除</button>}<button type="button" className="secondary" onClick={requestClose} disabled={saving}>キャンセル</button><button className="primary" disabled={!online || !title.trim() || saving}>{saving ? "保存中…" : "保存"}</button></footer></form></div>;
}

function MemosPage({ data, online, refresh }: PageProps) {
  const [selected, setSelected] = useState<Memo | null>(null); const [creating, setCreating] = useState(false); const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [memoOrderIds, setMemoOrderIds] = useState(() => [...data.memos].sort(compareMemos).map((memo) => memo.id));
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedMemoIds, setSelectedMemoIds] = useState<Set<string>>(new Set());
  const [bulkSaving, setBulkSaving] = useState(false);
  const [pinningMemoIds, setPinningMemoIds] = useState<Set<string>>(new Set());
  const [dragGhost, setDragGhost] = useState<{ memo: Memo; x: number; y: number; offsetX: number; offsetY: number; width: number; height: number } | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const dragGhostRef = useRef<HTMLDivElement | null>(null);
  const dragState = useRef<{ id: string; input: "pointer" | "touch"; pointerId: number; changed: boolean; active: boolean; startX: number; startY: number; x: number; y: number; memo: Memo; card: HTMLElement; timer: number } | null>(null);
  const dragLayout = useRef<{ baseOrder: string[]; slots: DOMRect[]; elements: Map<string, HTMLElement> } | null>(null);
  const settleMemoLayout = useRef(false);
  const touchListenerCleanup = useRef<(() => void) | null>(null);
  const suppressMemoClick = useRef<string | null>(null);
  const memoOrderRef = useRef(memoOrderIds);
  useMobileBodyScrollLock(Boolean(selected || creating));
  useEffect(() => {
    if (draggingId || savingOrder) return;
    const serverIds = [...data.memos].sort(compareMemos).map((memo) => memo.id);
    setMemoOrderIds((current) => {
      if (serverIds.length === current.length && serverIds.every((id, index) => id === current[index])) return current;
      memoOrderRef.current = serverIds;
      return serverIds;
    });
  }, [data.memos, draggingId, savingOrder]);
  useLayoutEffect(() => {
    if (!settleMemoLayout.current || !gridRef.current) return;
    gridRef.current.querySelectorAll<HTMLElement>("[data-memo-id]").forEach((element) => {
      element.style.transition = "";
      element.style.transform = "";
      element.style.willChange = "";
    });
    settleMemoLayout.current = false;
  }, [memoOrderIds]);
  useEffect(() => () => touchListenerCleanup.current?.(), []);
  const orderedMemos = memoOrderIds.map((id) => data.memos.find((memo) => memo.id === id)).filter((memo): memo is Memo => Boolean(memo));
  const selectedMemos = orderedMemos.filter((memo) => selectedMemoIds.has(memo.id));
  const allSelectedMemosPinned = selectedMemos.length > 0 && selectedMemos.every((memo) => memo.pinned);
  useEffect(() => {
    const availableIds = new Set(data.memos.map((memo) => memo.id));
    setSelectedMemoIds((current) => {
      const next = new Set([...current].filter((id) => availableIds.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [data.memos]);
  useEffect(() => {
    if (!selectionMode) return;
    const closeSelection = (event: KeyboardEvent) => { if (event.key === "Escape" && !bulkSaving) exitMemoSelection(); };
    window.addEventListener("keydown", closeSelection);
    return () => window.removeEventListener("keydown", closeSelection);
  }, [selectionMode, bulkSaving]);
  function highlight(id: string) { setHighlightedId(id); window.setTimeout(() => setHighlightedId((current) => current === id ? null : current), 1800); }
  function startMemoSelection(id?: string) {
    setSelectionMode(true);
    setSelectedMemoIds(id ? new Set([id]) : new Set());
  }
  function exitMemoSelection() {
    setSelectionMode(false);
    setSelectedMemoIds(new Set());
  }
  function toggleMemoSelection(id: string) {
    setSelectedMemoIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleAllMemoSelection() {
    setSelectedMemoIds(selectedMemoIds.size === orderedMemos.length ? new Set() : new Set(orderedMemos.map((memo) => memo.id)));
  }
  async function setSelectedMemosPinned() {
    if (!online || bulkSaving || !selectedMemos.length) return;
    setBulkSaving(true);
    try {
      const pinned = !allSelectedMemosPinned;
      for (const memo of selectedMemos) await repository!.updateMemo(memo.id, memo.revision, { pinned });
      await refresh();
      exitMemoSelection();
    } catch (cause) {
      await refresh();
      window.alert(cause instanceof Error ? cause.message : "選択したメモを更新できませんでした。");
    } finally {
      setBulkSaving(false);
    }
  }
  async function toggleMemoPinned(memo: Memo) {
    if (!online || bulkSaving || pinningMemoIds.has(memo.id)) return;
    setPinningMemoIds((current) => new Set(current).add(memo.id));
    try {
      await repository!.updateMemo(memo.id, memo.revision, { pinned: !memo.pinned });
      await refresh();
    } catch (cause) {
      await refresh();
      window.alert(cause instanceof Error ? cause.message : "お気に入りを更新できませんでした。");
    } finally {
      setPinningMemoIds((current) => {
        const next = new Set(current);
        next.delete(memo.id);
        return next;
      });
    }
  }
  async function removeSelectedMemos() {
    if (!online || bulkSaving || !selectedMemos.length) return;
    if (!window.confirm(`選択した${selectedMemos.length}件のメモを削除しますか？ 30日間はゴミ箱に残ります。`)) return;
    setBulkSaving(true);
    try {
      for (const memo of selectedMemos) await repository!.trash("memo", memo);
      await refresh();
      exitMemoSelection();
    } catch (cause) {
      await refresh();
      window.alert(cause instanceof Error ? cause.message : "選択したメモを削除できませんでした。");
    } finally {
      setBulkSaving(false);
    }
  }
  function clearMemoTransforms() {
    dragLayout.current?.elements.forEach((element) => {
      element.style.transition = "";
      element.style.transform = "";
      element.style.willChange = "";
    });
    dragLayout.current = null;
  }
  function activateMemoDrag(drag: NonNullable<typeof dragState.current>, capturePointer?: () => void) {
    if (dragState.current !== drag || !gridRef.current) return;
    drag.active = true;
    drag.startX = drag.x;
    drag.startY = drag.y;
    suppressMemoClick.current = drag.id;
    capturePointer?.();
    const cards = Array.from(gridRef.current.querySelectorAll<HTMLElement>("[data-memo-id]"));
    const baseOrder = cards.map((card) => card.dataset.memoId || "");
    const elements = new Map(cards.map((card) => [card.dataset.memoId || "", card]));
    cards.forEach((card) => {
      card.style.transition = "transform 240ms cubic-bezier(.2,.8,.2,1)";
      card.style.willChange = "transform";
    });
    dragLayout.current = { baseOrder, slots: cards.map((card) => card.getBoundingClientRect()), elements };
    memoOrderRef.current = baseOrder;
    const rect = drag.card.getBoundingClientRect();
    setDraggingId(drag.id);
    setDragGhost({ memo: drag.memo, x: drag.x, y: drag.y, offsetX: drag.x - rect.left, offsetY: drag.y - rect.top, width: rect.width, height: rect.height });
  }
  function moveMemoAt(clientX: number, clientY: number) {
    const drag = dragState.current;
    const layout = dragLayout.current;
    if (!drag?.active || !layout) return;
    if (dragGhostRef.current) {
      dragGhostRef.current.style.transform = `translate3d(${clientX - drag.startX}px, ${clientY - drag.startY}px, 0) scale(1.025) rotate(.35deg)`;
    }
    const nearestSlot = layout.slots
      .map((rect, index) => ({ index, distance: Math.hypot(clientX - (rect.left + rect.width / 2), clientY - (rect.top + rect.height / 2)) }))
      .sort((a, b) => a.distance - b.distance)[0];
    if (!nearestSlot) return;
    const withoutDragged = layout.baseOrder.filter((id) => id !== drag.id);
    const next = [...withoutDragged];
    next.splice(nearestSlot.index, 0, drag.id);
    if (next.every((id, index) => id === memoOrderRef.current[index])) return;
    drag.changed = true;
    memoOrderRef.current = next;
    for (const id of layout.baseOrder) {
      if (id === drag.id) continue;
      const from = layout.baseOrder.indexOf(id);
      const to = next.indexOf(id);
      const element = layout.elements.get(id);
      if (!element || from < 0 || to < 0) continue;
      element.style.transform = `translate3d(${layout.slots[to].left - layout.slots[from].left}px, ${layout.slots[to].top - layout.slots[from].top}px, 0)`;
    }
  }
  async function completeMemoDrag(drag: NonNullable<typeof dragState.current>) {
    window.clearTimeout(drag.timer);
    if (dragState.current === drag) dragState.current = null;
    if (!drag.active) return;
    setDraggingId(null);
    setDragGhost(null);
    if (!drag.changed) {
      clearMemoTransforms();
      return;
    }
    settleMemoLayout.current = true;
    setMemoOrderIds([...memoOrderRef.current]);
    dragLayout.current = null;
    setSavingOrder(true);
    try {
      await repository!.reorderMemos(memoOrderRef.current);
      await refresh();
    } catch {
      await refresh();
      window.alert("メモの並び順を保存できませんでした。通信状態を確認して、もう一度お試しください。");
    } finally {
      setSavingOrder(false);
    }
  }
  function beginMemoPress(memo: Memo, event: React.PointerEvent<HTMLButtonElement>) {
    if (event.pointerType === "touch") return;
    if (!online || savingOrder || selectionMode) return;
    const card = event.currentTarget.closest<HTMLElement>("[data-memo-id]");
    if (!card) return;
    const pointerId = event.pointerId;
    const button = event.currentTarget;
    const drag = { id: memo.id, input: "pointer" as const, pointerId, changed: false, active: false, startX: event.clientX, startY: event.clientY, x: event.clientX, y: event.clientY, memo, card, timer: 0 };
    drag.timer = window.setTimeout(() => {
      activateMemoDrag(drag, () => { try { button.setPointerCapture(pointerId); } catch { /* The pointer may already have ended. */ } });
    }, 280);
    dragState.current = drag;
  }
  function moveMemoPress(event: React.PointerEvent<HTMLButtonElement>) {
    if (event.pointerType === "touch") return;
    const drag = dragState.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    drag.x = event.clientX;
    drag.y = event.clientY;
    if (!drag.active) {
      if (Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) > 8) {
        window.clearTimeout(drag.timer);
        dragState.current = null;
      }
      return;
    }
    event.preventDefault();
    moveMemoAt(event.clientX, event.clientY);
  }
  async function finishMemoPress(event: React.PointerEvent<HTMLButtonElement>) {
    if (event.pointerType === "touch") return;
    const drag = dragState.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    await completeMemoDrag(drag);
  }
  function beginMemoTouch(memo: Memo, event: React.TouchEvent<HTMLButtonElement>) {
    if (!online || savingOrder || selectionMode || dragState.current) return;
    const touch = event.changedTouches[0];
    const card = event.currentTarget.closest<HTMLElement>("[data-memo-id]");
    if (!touch || !card) return;
    const press = { id: memo.id, input: "touch" as const, pointerId: touch.identifier, changed: false, active: false, startX: touch.clientX, startY: touch.clientY, x: touch.clientX, y: touch.clientY, memo, card, timer: 0 };
    const cleanup = () => {
      document.removeEventListener("touchmove", onMove, true);
      document.removeEventListener("touchend", onEnd, true);
      document.removeEventListener("touchcancel", onEnd, true);
      if (touchListenerCleanup.current === cleanup) touchListenerCleanup.current = null;
    };
    const onMove = (nativeEvent: TouchEvent) => {
      const currentTouch = Array.from(nativeEvent.touches).find((item) => item.identifier === press.pointerId);
      if (!currentTouch || dragState.current !== press) return;
      press.x = currentTouch.clientX;
      press.y = currentTouch.clientY;
      if (!press.active) {
        if (Math.hypot(press.x - press.startX, press.y - press.startY) <= 8) return;
        window.clearTimeout(press.timer);
        dragState.current = null;
        cleanup();
        return;
      }
      nativeEvent.preventDefault();
      moveMemoAt(press.x, press.y);
    };
    const onEnd = (nativeEvent: TouchEvent) => {
      if (!Array.from(nativeEvent.changedTouches).some((item) => item.identifier === press.pointerId)) return;
      cleanup();
      void completeMemoDrag(press);
    };
    press.timer = window.setTimeout(() => {
      if (dragState.current !== press) return;
      activateMemoDrag(press);
      navigator.vibrate?.(18);
    }, 360);
    dragState.current = press;
    touchListenerCleanup.current?.();
    touchListenerCleanup.current = cleanup;
    document.addEventListener("touchmove", onMove, { passive: false, capture: true });
    document.addEventListener("touchend", onEnd, { passive: false, capture: true });
    document.addEventListener("touchcancel", onEnd, { passive: false, capture: true });
  }
  return <section className="page">
    <PageHeader eyebrow="MEMOS" title="メモ"><div className="memo-page-tools"><span className="count-pill">{data.memos.length} 件</span>{!selectionMode && <button type="button" className="secondary memo-select-button" disabled={!data.memos.length || savingOrder} onClick={() => startMemoSelection()}>選択</button>}</div></PageHeader>
    {selectionMode && <div className="memo-selection-toolbar" role="toolbar" aria-label="選択したメモの操作">
      <button type="button" className="memo-selection-close" aria-label="選択を終了" disabled={bulkSaving} onClick={exitMemoSelection}>×</button>
      <strong>{selectedMemoIds.size}件選択</strong>
      <button type="button" disabled={bulkSaving || !orderedMemos.length} onClick={toggleAllMemoSelection}>{selectedMemoIds.size === orderedMemos.length ? "全解除" : "すべて選択"}</button>
      <button type="button" disabled={!online || bulkSaving || !selectedMemos.length} onClick={() => void setSelectedMemosPinned()}>{allSelectedMemosPinned ? "★ 解除" : "★ ピン留め"}</button>
      <button type="button" className="danger" disabled={!online || bulkSaving || !selectedMemos.length} onClick={() => void removeSelectedMemos()}>削除</button>
    </div>}
    <div ref={gridRef} className={`memo-grid ${draggingId ? "is-reordering" : ""} ${selectionMode ? "is-selecting" : ""}`}>{orderedMemos.map((memo) => {
      const memoSelected = selectedMemoIds.has(memo.id);
      const displayTitle = memoDisplayTitle(memo);
      const pinning = pinningMemoIds.has(memo.id);
      return <article data-memo-id={memo.id} className={`memo-card ${draggingId === memo.id ? "dragging" : ""} ${highlightedId === memo.id ? "just-created" : ""} ${memoSelected ? "selected" : ""}`} key={memo.id}>
        <button type="button" className="memo-open" aria-pressed={selectionMode ? memoSelected : undefined} aria-label={`${displayTitle}。${selectionMode ? memoSelected ? "選択済み" : "選択する" : "開く。長押しで移動"}`} onClick={() => { if (suppressMemoClick.current === memo.id) { suppressMemoClick.current = null; return; } if (selectionMode) toggleMemoSelection(memo.id); else setSelected(memo); }} disabled={savingOrder || bulkSaving || pinning} onTouchStart={(event) => beginMemoTouch(memo, event)} onPointerDown={(event) => beginMemoPress(memo, event)} onPointerMove={moveMemoPress} onPointerUp={(event) => void finishMemoPress(event)} onPointerCancel={(event) => void finishMemoPress(event)}>
          {selectionMode && <span className={`memo-selection-check ${memoSelected ? "checked" : ""}`} aria-hidden="true">{memoSelected ? "✓" : ""}</span>}
          {memo.pinned && <span className="memo-pin">★</span>}
          <h2>{displayTitle}</h2><p>{memoPreviewBody(memo.body)}</p><small>{new Date(memo.updated_at).toLocaleString("ja-JP")}</small>
        </button>
        {!selectionMode && <button type="button" className={`memo-pin-toggle ${memo.pinned ? "active" : ""}`} aria-label={memo.pinned ? `${displayTitle}のお気に入りを解除` : `${displayTitle}をお気に入りに追加`} title={memo.pinned ? "お気に入りを解除" : "お気に入りに追加"} disabled={!online || savingOrder || bulkSaving || pinning} onClick={() => void toggleMemoPinned(memo)}>{memo.pinned ? "★" : "☆"}</button>}
      </article>;
    })}</div>
    {dragGhost && <div ref={dragGhostRef} className="memo-drag-ghost" style={{ left: dragGhost.x - dragGhost.offsetX, top: dragGhost.y - dragGhost.offsetY, width: dragGhost.width, height: dragGhost.height }}>{dragGhost.memo.pinned && <span>★</span>}<h2>{memoDisplayTitle(dragGhost.memo)}</h2><p>{memoPreviewBody(dragGhost.memo.body)}</p><small>{new Date(dragGhost.memo.updated_at).toLocaleString("ja-JP")}</small></div>}
    {!data.memos.length && <Empty text="メモはまだありません。"/>}
    {!selectionMode && <button className="create-fab" type="button" aria-label="メモを追加" title="メモを追加" disabled={!online} onClick={() => setCreating(true)}>＋</button>}
    {(selected || creating) && <MemoEditor memo={selected} online={online} close={() => { setSelected(null); setCreating(false); }} refresh={refresh} onCreated={highlight}/>}
  </section>;
}

function MemoEditor({ memo, online, close, refresh, onCreated }: { memo: Memo | null; online: boolean; close: () => void; refresh: () => Promise<void>; onCreated?: (id: string) => void }) {
  const [title, setTitle] = useState(memo?.title || ""); const [body, setBody] = useState(memo?.body || ""); const [pinned, setPinned] = useState(memo?.pinned || false); const [saving, setSaving] = useState(false);
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "error">("idle");
  const memoFieldRef = useRef<HTMLDivElement | null>(null);
  const dirty = title !== (memo?.title || "") || body !== (memo?.body || "") || pinned !== (memo?.pinned || false);
  function requestClose() { if (!dirty || window.confirm("入力内容を破棄しますか？")) close(); }
  const swipeToClose = useSwipeRightToClose(requestClose, saving);
  useEffect(() => { const closeOnEscape = (event: KeyboardEvent) => { if (event.key === "Escape" && !saving) requestClose(); }; window.addEventListener("keydown", closeOnEscape); return () => window.removeEventListener("keydown", closeOnEscape); }, [dirty, saving]);
  async function save(e: FormEvent) { e.preventDefault(); if (saving || (!title.trim() && !body.trim())) return; setSaving(true); try { const storedTitle = title.trim(); if (memo) await repository!.updateMemo(memo.id, memo.revision, { title: storedTitle, body: body.trim(), pinned }); else { const created = await repository!.createMemo({ title: storedTitle, body: body.trim(), pinned }); onCreated?.(created.id); } await refresh(); close(); } finally { setSaving(false); } }
  async function copyBody() {
    if (!body) return;
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(body);
      else {
        const field = document.createElement("textarea");
        field.value = body;
        field.style.position = "fixed";
        field.style.opacity = "0";
        document.body.appendChild(field);
        field.select();
        const copied = document.execCommand("copy");
        field.remove();
        if (!copied) throw new Error("copy failed");
      }
      setCopyStatus("copied");
    } catch {
      setCopyStatus("error");
    }
  }
  async function remove() { if (!memo || !window.confirm(`「${memoDisplayTitle(memo)}」を削除しますか？ 30日間はゴミ箱に残ります。`)) return; await repository!.trash("memo", memo); await refresh(); close(); }
  return <div className="modal-backdrop entry-modal" {...swipeToClose} onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) requestClose(); }}>
    <form className="editor-card narrow memo-editor-card" onSubmit={save}>
      <header><div><p className="eyebrow">{memo ? "EDIT MEMO" : "NEW MEMO"}</p><h2>{memo ? "メモ編集" : "新しいメモ"}</h2></div><div className="memo-header-actions"><button type="button" className={`memo-copy-icon ${copyStatus}`} disabled={!body || saving} onClick={() => void copyBody()} aria-label={copyStatus === "copied" ? "本文をコピーしました" : copyStatus === "error" ? "本文をコピーできませんでした" : "本文をコピー"} title={copyStatus === "copied" ? "コピーしました" : copyStatus === "error" ? "コピーできませんでした" : "本文をコピー"}><svg aria-hidden="true" viewBox="0 0 24 24"><rect x="8" y="4" width="11" height="13" rx="2"/><path d="M16 20H7a2 2 0 0 1-2-2V8"/></svg></button><button type="button" className="icon-button" onClick={requestClose} aria-label="閉じる" disabled={saving}>×</button></div></header>
      <div ref={memoFieldRef} className="memo-combined-field" role="group" aria-label="メモ">
        <input className="memo-title-line" aria-label="タイトル（任意）" value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(event) => { if (event.key !== "Enter" || event.nativeEvent.isComposing) return; event.preventDefault(); memoFieldRef.current?.querySelector<HTMLElement>(".memo-rich-editor")?.focus(); }} placeholder="# タイトル（任意）" maxLength={200}/>
        <div className="memo-body-field memo-body-inline"><Suspense fallback={<div className="memo-editor-loading">エディタを準備中…</div>}><MemoMarkdownEditor value={body} onChange={setBody} autoFocus/></Suspense></div>
      </div>
      <small className="markdown-notation-help memo-notation-help">{MARKDOWN_NOTATION_HELP}</small>
      <label className="check-label"><input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)}/>ピン留め</label>
      <footer>{memo && <button type="button" className="danger" disabled={!online || saving} onClick={remove}>削除</button>}<button type="button" className="secondary" onClick={requestClose} disabled={saving}>キャンセル</button><button className="primary" disabled={!online || (!title.trim() && !body.trim()) || saving}>{saving ? "保存中…" : "保存"}</button></footer>
    </form>
  </div>;
}

function Empty({ text }: { text: string }) { return <p className="empty">{text}</p>; }
